import { useState, useEffect, useCallback } from "react";
import { supabase } from "./supabaseClient";

// ─── CONSTANTS ────────────────────────────────────────────────────────────────

const TRUST_PROMPTS_COUPLES = [
  { id: "obedience", question: "Am I willing to walk in obedience — even when the outcome isn't guaranteed?", followup: { id: "obedience_note", placeholder: "What do I need to let go of to do that?" } },
  { id: "no_easy", question: "Do I accept that there is no easy option — every path has a cost?", followup: null },
  { id: "ups_downs", question: "Do I recognize that either path will have both predictable and unpredictable challenges?", followup: null },
  { id: "together", question: "Do I believe we're in this together — and that trials aren't anyone's fault?", followup: { id: "together_note", placeholder: "Is there anything I need to say to my partner right now?" } },
  { id: "protection", question: "Do we trust that God can protect us and meet us wherever we go?", followup: null },
  { id: "provision", question: "Do we believe God will provide through this process — even in the hard moments?", followup: { id: "provision_note", placeholder: "What does that trust look like practically for us?" } },
];

const TRUST_PROMPTS_SOLO = [
  { id: "obedience", question: "Am I willing to walk in obedience — even when the outcome isn't guaranteed?", followup: { id: "obedience_note", placeholder: "What do I need to let go of to do that?" } },
  { id: "no_easy", question: "Do I accept that there is no easy option — every path has a cost?", followup: null },
  { id: "ups_downs", question: "Do I recognize that either path will have both predictable and unpredictable challenges?", followup: null },
  { id: "alone", question: "Am I making this decision based on faith — not just what feels comfortable or safe?", followup: { id: "alone_note", placeholder: "What's the honest answer when I sit with this quietly?" } },
  { id: "protection", question: "Do I trust that God can protect me and meet me wherever I go?", followup: null },
  { id: "provision", question: "Do I believe God will provide through this — even in the hard moments?", followup: { id: "provision_note", placeholder: "What does that trust actually look like for me right now?" } },
];

const VISION_BUILDER_PROMPTS = [
  { id: "vb_years", label: "Where do you see yourself in 20 years?", placeholder: "Think about where you live, how you spend your time, who surrounds you…", hint: "This matters because if you don't make decisions pointing toward this, you'll never end up there." },
  { id: "vb_important", label: "What are the most important things to you?", placeholder: "Faith, family, freedom, legacy, community, creativity…", hint: "Not what sounds good — what actually drives your decisions when you're being honest." },
  { id: "vb_success", label: "What does success look like for your family?", placeholder: "Not just financially — relationally, spiritually, generationally…", hint: "Define it on your terms, not someone else's." },
];

const TRUST_PROMPTS = TRUST_PROMPTS_COUPLES; // default, overridden by mode

const STEPS = [
  {
    id: "decision", gate: false, number: "01",
    label: "What are you deciding?",
    tagline: "Name it before you work through it.",
    tagline_solo: "Name it before you work through it.",
    description: "Before anything else — get clear on what you're actually choosing between. You don't need to have it figured out yet. Just name the options so you have something concrete to work through.",
    description_solo: "Before anything else — get clear on what you're actually choosing between. You don't need to have it figured out yet. Just name the options so you have something concrete to work through.",
  },
  {
    id: "spiritual", gate: true, number: "02",
    label: "Spiritual Alignment", tagline: "Before anything else — get quiet together.",
    tagline_solo: "Before anything else — get quiet.",
    description: "Big decisions made in noise rarely hold up. This step isn't about having perfect clarity — it's about making sure you're both seeking it. God's direction tends to be steady and consistent, not frantic or pressured.",
    description_solo: "Big decisions made in noise rarely hold up. This isn't about having perfect clarity — it's about making sure you're seeking it. God's direction tends to be steady and consistent, not frantic or pressured.",
    questions: [
      { id: "prayed", text: "We've each prayed about this individually, and together.", solo: "I've prayed about this — individually and intentionally." },
      { id: "fasted", text: "We've intentionally created space — away from noise, opinions, and pressure.", solo: "I've intentionally created space — away from noise, opinions, and pressure." },
      { id: "themes", text: "We're noticing consistent direction, not just emotional highs and lows.", solo: "I'm noticing consistent direction, not just emotional highs and lows." },
      { id: "scripture", text: "What we're sensing aligns with Scripture and our family's calling.", solo: "What I'm sensing aligns with Scripture and my calling." },
    ],
    gate_question: "Are both of you sensing enough clarity and peace to move forward?",
    gate_question_solo: "Do you have enough clarity and peace to move forward?",
    gate_pass: "You have a shared foundation. That matters — keep going.",
    gate_pass_solo: "You have a foundation. That matters — keep going.",
    gate_fail: "That's okay. Don't rush this. More time in prayer isn't weakness — it's wisdom.",
    non_negotiable_tooltip: "If there's no spiritual alignment here, the rest of the process won't hold. Big decisions need a foundation — not just logic or emotion. This is the anchor everything else depends on.",
  },
  {
    id: "vision", gate: true, number: "03",
    label: "Family Vision Alignment", tagline: "Zoom out. What are you actually building?",
    tagline_solo: "Zoom out. What are you actually building toward?",
    description: "Day-to-day comfort and long-term vision aren't always the same thing. This step isn't about what feels easiest right now — it's about whether this decision moves you toward the life you both said you wanted to build.",
    description_solo: "Day-to-day comfort and long-term vision aren't always the same thing. This isn't about what feels easiest right now — it's about whether this decision moves you toward the life you want to build.",
    vision_anchors: [],
    gate_question: "Honestly — does this decision move your family toward your shared vision?",
    gate_question_solo: "Honestly — does this decision move you toward the life you want to build?",
    gate_pass: "It points in the right direction. That's a meaningful signal.",
    gate_fail: "If it doesn't serve the vision, it's worth asking why you're still considering it.",
    non_negotiable_tooltip: "If this decision doesn't serve your long-term vision, short-term comfort will win by default. This step makes sure you're deciding from the right direction — not just the path of least resistance.",
  },
  {
    id: "reality", gate: false, number: "04",
    label: "Reality Mapping", tagline: "Every option costs something. Let's name it.",
    description: "This isn't about being negative — it's about being honest. The goal isn't to find the painless path. It's to understand what you're actually choosing between so neither of you feels blindsided later.",
    description_solo: "This isn't about being negative — it's about being honest. The goal isn't to find the painless path. It's to understand what you're actually choosing between.",
  },
  {
    id: "emotional", gate: false, number: "05",
    label: "Emotional Processing", tagline: "This step is for both of you — not just one.",
    tagline_solo: "Name what's really going on underneath.",
    description: "Feelings aren't obstacles to good decisions — unexamined feelings are. This is your space to name what's really going on underneath the surface, without judgment and without rushing to fix it.",
    description_solo: "Feelings aren't obstacles to good decisions — unexamined feelings are. This is your space to name what's really going on underneath the surface, without rushing to fix it.",
  },
  {
    id: "trust", gate: true, number: "06",
    label: "Realignment & Recommitment", tagline: "Come back to each other before you decide.",
    tagline_solo: "Come back to yourself before you decide.",
    description: "The last two steps were hard. That's okay — it means you're being real with each other. Before you move toward a decision, take a breath and answer these individually. There's no pressure to agree on everything right now. Just be honest.",
    description_solo: "The last two steps were hard. That's okay — it means you're being real with yourself. Before you decide, take a breath and answer these honestly.",
    non_negotiable_tooltip: "This step exists because emotional and logical processing can pull you in different directions. Recommitting here — to faith over fear, to each other — grounds the decision before you make it.",
  },
  {
    id: "threshold", gate: false, number: "07",
    label: "Decision Threshold", tagline: "You've done the work. Now make the call.",
    description: "Waiting for perfect certainty isn't wisdom — it's avoidance. If you've worked through each step honestly, you have enough to decide. Set a deadline and honor it.",
    note: "Clarity is the goal — not comfort. You won't feel 100% ready, and that's completely normal.",
    checks: [
      { id: "s", label: "We've prayed and feel spiritually grounded" },
      { id: "v", label: "This decision serves our family vision" },
      { id: "r", label: "We both understand the real trade-offs" },
      { id: "e", label: "Our emotions have been heard and honored" },
      { id: "f", label: "We're moving in faith, not running from fear" },
    ],
    checks_solo: [
      { id: "s", label: "I've prayed and feel spiritually grounded" },
      { id: "v", label: "This decision serves my long-term vision" },
      { id: "r", label: "I understand the real trade-offs honestly" },
      { id: "e", label: "I've sat with my emotions and named them" },
      { id: "f", label: "I'm moving in faith, not running from fear" },
    ],
  },
  {
    id: "commit", gate: false, number: "08",
    label: "Unified Commitment", tagline: "You chose this. Now own it together.",
    tagline_solo: "You chose this. Now own it.",
    description: "A decision made together deserves to be honored together. No revisiting it every week. No quiet scorekeeping. You walked through something hard — now walk forward.",
    description_solo: "You made this decision. Honor it. No revisiting it every week. No second-guessing. You walked through something hard — now walk forward.",
    pledges: [
      { id: "together", text: "We made this decision together — it belongs to both of us." },
      { id: "build", text: "We will build on this decision, not second-guess it." },
      { id: "forward", text: "We won't re-open this unless something significant and new changes." },
    ],
    pledges_solo: [
      { id: "together", text: "I made this decision clearly and intentionally." },
      { id: "build", text: "I will build on this decision, not second-guess it." },
      { id: "forward", text: "I won't re-open this unless something significant and new changes." },
    ],
  },
];

const FEAR_OPTIONS = [
  { id: "financial", label: "Financial risk", emoji: "💸" },
  { id: "community", label: "Losing community", emoji: "👥" },
  { id: "kids", label: "Impact on kids", emoji: "👶" },
  { id: "unknown", label: "Too many unknowns", emoji: "❓" },
  { id: "failure", label: "Fear of failure", emoji: "😰" },
  { id: "regret", label: "Regret either way", emoji: "🔄" },
  { id: "identity", label: "Loss of routine", emoji: "🪞" },
  { id: "family_opinion", label: "What family will think", emoji: "👨‍👩‍👧" },
];

const FLOW_NODES = [
  { label: "Spiritual Alignment?", pass: "Continue", fail: "Pause & Pray" },
  { label: "Family Vision Alignment?", pass: "Continue", fail: "Reject Option" },
  { label: "Reality Check Clear?", pass: "Continue", fail: "Map Trade-offs" },
  { label: "Emotions Processed?", pass: "Continue", fail: "Surface Fears" },
  { label: "Faith vs Fear?", pass: "Continue", fail: "Address Spiritually" },
  { label: "Threshold Met?", pass: "Decide", fail: "Clarify Criteria" },
  { label: "DECIDE & COMMIT", final: true },
];

const LOCAL_KEY = "decidewell_v3";

// ─── COLOR PALETTE ────────────────────────────────────────────────────────────
const C = {
  // Teal family
  tealDark:    "#085041",
  teal:        "#0F6E56",
  tealMid:     "#1D9E75",
  tealLight:   "#5DCAA5",
  tealPale:    "#9FE1CB",
  tealBg:      "#E1F5EE",
  // Gold family
  goldDark:    "#633806",
  gold:        "#BA7517",
  goldMid:     "#EF9F27",
  goldLight:   "#FAC775",
  goldBg:      "#FAEEDA",
  // Ivory / base
  ivory:       "#F9F7F2",
  ivoryDeep:   "#F2EFE8",
  // Charcoal
  charcoal:    "#2C2C2A",
  charcoalMid: "#5F5E5A",
  charcoalLight:"#B4B2A9",
  charcoalBg:  "#F1EFE8",
  // Semantic (kept for fear/pro/con existing)
  green:       "#2e7d32",
  greenBg:     "#e8f5e9",
  greenBorder: "#81c784",
  red:         "#c0392b",
  orange:      "#e65100",
  orangeBg:    "#fff8f1",
  orangeBorder:"#ffcc80",
};

const S = {
  body: { fontSize: 14, color: C.charcoalMid, fontFamily: "'DM Sans', sans-serif", lineHeight: 1.65, margin: 0 },
  label: { fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase", color: C.charcoalLight, fontFamily: "'DM Sans', sans-serif", margin: 0, fontWeight: 600 },
  input: { border: `1.5px solid ${C.tealPale}`, borderRadius: 12, padding: "11px 14px", fontSize: 14, fontFamily: "'DM Sans', sans-serif", color: C.charcoal, outline: "none", background: "#fff" },
};

// ─── SUPABASE HELPERS ─────────────────────────────────────────────────────────

async function dbSaveSession(code, role, answers, stepIdx) {
  try {
    await supabase.from("sessions").upsert(
      { code, role, answers, step_idx: stepIdx, updated_at: new Date().toISOString() },
      { onConflict: "code,role" }
    );
  } catch (e) {}
}

async function dbLoadSession(code, role) {
  try {
    const { data } = await supabase
      .from("sessions")
      .select("*")
      .eq("code", code)
      .eq("role", role)
      .single();
    return data;
  } catch (e) { return null; }
}

async function dbLoadPartner(code, myRole) {
  const partnerRole = myRole === "person_a" ? "person_b" : "person_a";
  try {
    const { data } = await supabase
      .from("sessions")
      .select("*")
      .eq("code", code)
      .eq("role", partnerRole)
      .single();
    return data;
  } catch (e) { return null; }
}

// ─── LOCAL STORAGE HELPERS ────────────────────────────────────────────────────

function localSave(data) {
  try { localStorage.setItem(LOCAL_KEY, JSON.stringify(data)); } catch (e) {}
}

function localLoad() {
  try { const d = localStorage.getItem(LOCAL_KEY); return d ? JSON.parse(d) : null; } catch (e) { return null; }
}

function localClear() {
  try { localStorage.removeItem(LOCAL_KEY); } catch (e) {}
}

function genCode() {
  return Math.random().toString(36).substring(2, 6).toUpperCase();
}

// ─── EXPANDABLE TEXTAREA ──────────────────────────────────────────────────────

function ExpandableTextarea({ value, onChange, placeholder, minHeight = 75, label }) {
  const [expanded, setExpanded] = useState(false);

  const handleKey = useCallback((e) => {
    if (e.key === "Escape") setExpanded(false);
  }, []);

  useEffect(() => {
    if (expanded) {
      document.addEventListener("keydown", handleKey);
      document.body.style.overflow = "hidden";
    } else {
      document.removeEventListener("keydown", handleKey);
      document.body.style.overflow = "";
    }
    return () => {
      document.removeEventListener("keydown", handleKey);
      document.body.style.overflow = "";
    };
  }, [expanded, handleKey]);

  const textareaStyle = {
    ...S.input,
    width: "100%",
    boxSizing: "border-box",
    resize: "none",
    lineHeight: 1.6,
    fontFamily: "'DM Sans', sans-serif",
  };

  return (
    <>
      {/* Inline version */}
      <div style={{ position: "relative" }}>
        <textarea
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder={placeholder}
          style={{ ...textareaStyle, minHeight, paddingRight: 38 }}
        />
        <button
          onClick={() => setExpanded(true)}
          title="Expand"
          style={{
            position: "absolute", top: 8, right: 8,
            background: "#f0f0f0", border: "none", borderRadius: 6,
            width: 26, height: 26, cursor: "pointer", outline: "none",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 13, color: "#888", transition: "background 0.15s",
          }}
          onMouseEnter={e => e.currentTarget.style.background = "#e0e0e0"}
          onMouseLeave={e => e.currentTarget.style.background = "#f0f0f0"}
        >⤢</button>
      </div>

      {/* Expanded modal overlay */}
      {expanded && (
        <div
          onClick={() => setExpanded(false)}
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
            display: "flex", alignItems: "center", justifyContent: "center",
            zIndex: 200, padding: "20px 16px",
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: "#fff", borderRadius: 20, padding: "22px 20px",
              width: "100%", maxWidth: 560,
              display: "flex", flexDirection: "column", gap: 12,
              boxShadow: "0 8px 40px rgba(0,0,0,0.18)",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              {label && (
                <p style={{ ...S.label, margin: 0 }}>{label}</p>
              )}
              <button
                onClick={() => setExpanded(false)}
                style={{
                  marginLeft: "auto", background: "#f0f0f0", border: "none",
                  borderRadius: 8, padding: "6px 12px", fontSize: 12,
                  fontFamily: "'DM Sans', sans-serif", color: "#555",
                  cursor: "pointer", outline: "none", display: "flex", alignItems: "center", gap: 5,
                }}
              >Done ✓</button>
            </div>
            <textarea
              autoFocus
              value={value}
              onChange={e => onChange(e.target.value)}
              placeholder={placeholder}
              style={{
                ...textareaStyle,
                minHeight: 260,
                fontSize: 15,
                lineHeight: 1.7,
                border: "1.5px solid #e0e0e0",
                borderRadius: 12,
                padding: "14px 16px",
              }}
            />
            <p style={{ fontSize: 11, color: "#ccc", fontFamily: "'DM Sans', sans-serif", margin: 0, textAlign: "right" }}>
              Press Esc or tap Done to close
            </p>
          </div>
        </div>
      )}
    </>
  );
}

// ─── SMALL COMPONENTS ─────────────────────────────────────────────────────────

function CheckRow({ checked, onToggle, text }) {
  return (
    <button onClick={onToggle} style={{
      display: "flex", alignItems: "flex-start", gap: 12,
      background: checked ? C.teal : C.ivory,
      border: `1.5px solid ${checked ? C.teal : C.tealPale}`,
      borderRadius: 12, padding: "13px 15px",
      cursor: "pointer", textAlign: "left", width: "100%",
      transition: "all 0.18s ease", outline: "none",
    }}>
      <div style={{
        width: 18, height: 18, borderRadius: 5, flexShrink: 0, marginTop: 1,
        border: `1.5px solid ${checked ? C.tealLight : C.tealPale}`,
        background: checked ? C.tealLight : "transparent",
        display: "flex", alignItems: "center", justifyContent: "center",
        transition: "all 0.15s",
      }}>
        {checked && <span style={{ fontSize: 10, color: C.tealDark, fontWeight: 700 }}>✓</span>}
      </div>
      <span style={{ fontSize: 14, lineHeight: 1.5, fontFamily: "'DM Sans', sans-serif", color: checked ? "#fff" : C.charcoal, fontWeight: checked ? 500 : 400 }}>{text}</span>
    </button>
  );
}

function GateResult({ pass, passMsg, failMsg }) {
  return (
    <div style={{ borderRadius: 14, padding: "14px 18px", background: pass ? C.teal : C.goldBg, border: pass ? "none" : `1.5px solid ${C.goldLight}`, display: "flex", alignItems: "center", gap: 12, marginTop: 6 }}>
      <span style={{ fontSize: 20 }}>{pass ? "✅" : "⏸"}</span>
      <span style={{ fontSize: 14, fontWeight: 600, fontFamily: "'DM Sans', sans-serif", color: pass ? "#fff" : C.goldDark, lineHeight: 1.4 }}>{pass ? passMsg : failMsg}</span>
    </div>
  );
}

function GateButtons({ value, onChange, yesLabel = "✓ Yes", noLabel = "Not yet" }) {
  return (
    <div style={{ display: "flex", gap: 8 }}>
      {[["yes", yesLabel], ["no", noLabel]].map(([v, lbl]) => (
        <button key={v} onClick={() => onChange(v)} style={{
          flex: 1, padding: "12px", borderRadius: 12,
          border: `1.5px solid ${value === v ? (v === "yes" ? C.teal : C.gold) : C.tealPale}`,
          background: value === v ? (v === "yes" ? C.teal : C.goldBg) : C.ivory,
          color: value === v ? (v === "yes" ? "#fff" : C.goldDark) : C.charcoalMid,
          fontSize: 14, fontWeight: 600, fontFamily: "'DM Sans', sans-serif",
          cursor: "pointer", transition: "all 0.18s", outline: "none",
        }}>{lbl}</button>
      ))}
    </div>
  );
}

function Dots({ current, total }) {
  return (
    <div style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 26 }}>
      {Array.from({ length: total }).map((_, i) => (
        <div key={i} style={{
          width: i === current ? 22 : 6, height: 6, borderRadius: 3,
          background: i < current ? C.tealLight : i === current ? C.teal : C.charcoalBg,
          transition: "all 0.3s ease",
        }} />
      ))}
    </div>
  );
}

function SaveBadge({ lastSaved }) {
  if (!lastSaved) return null;
  const mins = Math.floor((Date.now() - lastSaved) / 60000);
  const label = mins < 1 ? "Saved just now" : `Saved ${mins}m ago`;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, color: C.teal, fontFamily: "'DM Sans', sans-serif" }}>
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: C.tealMid, display: "inline-block" }} />
      {label}
    </div>
  );
}

// ─── NON-NEGOTIABLE TOOLTIP ───────────────────────────────────────────────────

function NonNegotiableTooltip({ text }) {
  const [show, setShow] = useState(false);
  return (
    <div style={{ position: "relative", display: "inline-block" }}>
      <button
        onClick={() => setShow(s => !s)}
        style={{ fontSize: 10, background: C.teal, color: C.tealBg, borderRadius: 50, padding: "2px 9px", fontWeight: 600, fontFamily: "'DM Sans', sans-serif", letterSpacing: "0.08em", border: "none", cursor: "pointer", outline: "none" }}
      >NON-NEGOTIABLE ⓘ</button>
      {show && (
        <>
          <div onClick={() => setShow(false)} style={{ position: "fixed", inset: 0, zIndex: 50 }} />
          <div style={{
            position: "absolute", top: "calc(100% + 8px)", left: 0, zIndex: 60,
            background: C.tealDark, borderRadius: 12, padding: "12px 14px",
            width: 260, boxShadow: "0 4px 20px rgba(0,0,0,0.2)",
          }}>
            <p style={{ fontSize: 12, fontFamily: "'DM Sans', sans-serif", lineHeight: 1.6, margin: 0, color: C.tealBg }}>{text}</p>
            <div style={{ position: "absolute", top: -6, left: 14, width: 0, height: 0, borderLeft: "6px solid transparent", borderRight: "6px solid transparent", borderBottom: `6px solid ${C.tealDark}` }} />
          </div>
        </>
      )}
    </div>
  );
}

// ─── STEP INFO OVERLAY ────────────────────────────────────────────────────────

function StepInfoBanner({ description }) {
  const [dismissed, setDismissed] = useState(false);
  if (dismissed) return null;
  return (
    <div style={{ background: C.tealBg, border: `1.5px solid ${C.tealPale}`, borderLeft: `4px solid ${C.tealMid}`, borderRadius: 14, padding: "14px 16px", marginBottom: 4 }}>
      <p style={{ fontSize: 13, color: C.tealDark, fontFamily: "'DM Sans', sans-serif", lineHeight: 1.65, margin: 0 }}>{description}</p>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 10 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
          <span style={{ fontSize: 16, color: C.tealMid }}>↓</span>
          <span style={{ fontSize: 11, color: C.teal, fontFamily: "'DM Sans', sans-serif" }}>Answer below</span>
        </div>
        <button onClick={() => setDismissed(true)} style={{ background: "none", border: "none", fontSize: 11, color: C.tealLight, fontFamily: "'DM Sans', sans-serif", cursor: "pointer", outline: "none" }}>Got it ✕</button>
      </div>
    </div>
  );
}

function DecisionSetup({ answers, set, isSolo }) {
  const [showThird, setShowThird] = useState(!!(answers.opt_c));
  const optA = answers.opt_a || "";
  const optB = answers.opt_b || "";
  const optC = answers.opt_c || "";
  const canContinue = optA.trim().length > 1 && optB.trim().length > 1;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{ background: C.tealBg, border: `1.5px solid ${C.tealPale}`, borderLeft: `4px solid ${C.tealMid}`, borderRadius: 14, padding: "14px 16px" }}>
        <p style={{ fontSize: 13, color: C.tealDark, fontFamily: "'DM Sans', sans-serif", lineHeight: 1.65, margin: 0 }}>
          {isSolo
            ? "You don't need to have it figured out yet. Just name what you're actually choosing between so you have something concrete to work through."
            : "You don't need to have it all figured out yet. Just name what you're actually choosing between so you both have something concrete to work through."}
        </p>
      </div>

      {/* Option A */}
      <div>
        <p style={{ fontSize: 12, fontWeight: 700, color: C.teal, fontFamily: "'DM Sans', sans-serif", letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 8 }}>Option A</p>
        <input
          value={optA}
          onChange={e => set({ opt_a: e.target.value })}
          placeholder="e.g. Stay in Denver"
          style={{ ...S.input, width: "100%", boxSizing: "border-box", fontSize: 15 }}
          autoFocus
        />
      </div>

      {/* Option B */}
      <div>
        <p style={{ fontSize: 12, fontWeight: 700, color: C.teal, fontFamily: "'DM Sans', sans-serif", letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 8 }}>Option B</p>
        <input
          value={optB}
          onChange={e => set({ opt_b: e.target.value })}
          placeholder="e.g. Move to Northwest Arkansas"
          style={{ ...S.input, width: "100%", boxSizing: "border-box", fontSize: 15 }}
        />
      </div>

      {/* Option C */}
      {showThird && (
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
            <p style={{ fontSize: 12, fontWeight: 700, color: C.teal, fontFamily: "'DM Sans', sans-serif", letterSpacing: "0.06em", textTransform: "uppercase", margin: 0 }}>Option C</p>
            <button onClick={() => { setShowThird(false); set({ opt_c: "" }); }} style={{ background: "none", border: "none", fontSize: 11, color: C.charcoalLight, fontFamily: "'DM Sans', sans-serif", cursor: "pointer", outline: "none" }}>Remove ✕</button>
          </div>
          <input
            value={optC}
            onChange={e => set({ opt_c: e.target.value })}
            placeholder="e.g. Delay the decision for 6 months"
            style={{ ...S.input, width: "100%", boxSizing: "border-box", fontSize: 15 }}
            autoFocus
          />
        </div>
      )}

      {!showThird && (
        <button onClick={() => setShowThird(true)} style={{
          background: "none", border: `1.5px dashed ${C.tealPale}`, borderRadius: 12,
          padding: "12px", fontSize: 13, fontFamily: "'DM Sans', sans-serif",
          color: C.tealLight, cursor: "pointer", outline: "none",
          display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
        }}>
          + Add a third option
        </button>
      )}

      {/* Summary chips once filled */}
      {canContinue && (
        <div style={{ background: C.ivory, border: `1.5px solid ${C.tealPale}`, borderRadius: 12, padding: "12px 14px" }}>
          <p style={{ fontSize: 11, color: C.teal, fontFamily: "'DM Sans', sans-serif", fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 8 }}>You're deciding between</p>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
            {[optA, optB, optC].filter(Boolean).map((opt, i) => (
              <span key={i} style={{ background: C.tealBg, color: C.tealDark, border: `1.5px solid ${C.tealPale}`, borderRadius: 50, padding: "5px 14px", fontSize: 13, fontFamily: "'DM Sans', sans-serif", fontWeight: 600 }}>{opt}</span>
            ))}
          </div>
        </div>
      )}

      {!canContinue && (
        <p style={{ fontSize: 12, color: C.charcoalLight, fontFamily: "'DM Sans', sans-serif", textAlign: "center", margin: 0 }}>
          Name at least two options to continue →
        </p>
      )}
    </div>
  );
}

// ─── STEP COMPONENTS ──────────────────────────────────────────────────────────

function Spiritual({ step, answers, set, isSolo }) {
  const checks = answers.sp_checks || {};
  const gate = answers.sp_gate;
  const questions = step.questions;
  const firstUnanswered = questions.findIndex(q => !checks[q.id]);
  const visibleUpTo = firstUnanswered === -1 ? questions.length : firstUnanswered;
  const all = firstUnanswered === -1;

  // Show "thoughts to discuss" box if any question unchecked or gate is "no"
  const hasUnchecked = questions.some(q => !checks[q.id]);
  const gateIsNo = gate === "no";
  const showDiscussBox = hasUnchecked || gateIsNo;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <StepInfoBanner description={isSolo ? step.description_solo : step.description} />
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {questions.slice(0, visibleUpTo + 1).map((q, i) => (
          <div key={q.id} style={{ animation: i === visibleUpTo ? "fadeUp 0.3s ease" : "none" }}>
            <CheckRow
              checked={!!checks[q.id]}
              text={isSolo ? q.solo : q.text}
              onToggle={() => set({ sp_checks: { ...checks, [q.id]: !checks[q.id] } })}
            />
          </div>
        ))}
      </div>

      {all && (
        <div style={{ marginTop: 8, animation: "fadeUp 0.3s ease" }}>
          <p style={{ ...S.label, marginBottom: 8 }}>{isSolo ? step.gate_question_solo : step.gate_question}</p>
          <GateButtons value={gate} onChange={v => set({ sp_gate: v })} yesLabel="✓ We have clarity" noLabel="Not yet" />
          {gate && <GateResult pass={gate === "yes"} passMsg={isSolo ? step.gate_pass_solo || step.gate_pass : step.gate_pass} failMsg={step.gate_fail} />}
        </div>
      )}

      {/* Thoughts to discuss later — appears when anything is unchecked or gate is "no" */}
      {showDiscussBox && (
        <div style={{ marginTop: 4, background: "#fff8f1", border: "1.5px solid #ffcc80", borderRadius: 14, padding: "14px 16px", animation: "fadeUp 0.3s ease" }}>
          <p style={{ fontSize: 13, fontWeight: 600, color: "#e65100", fontFamily: "'DM Sans', sans-serif", marginBottom: 4 }}>
            📋 Thoughts to discuss later
          </p>
          <p style={{ fontSize: 12, color: "#888", fontFamily: "'DM Sans', sans-serif", marginBottom: 10, lineHeight: 1.5 }}>
            {gateIsNo
              ? "You're not ready to move forward yet — and that's okay. Write down what's on your mind so it doesn't get lost."
              : "Some things aren't fully settled yet. Capture them here so you can come back to them later."}
          </p>
          <ExpandableTextarea
            value={answers.sp_discuss || ""}
            onChange={v => set({ sp_discuss: v })}
            placeholder="What needs more prayer, conversation, or clarity before you can move forward?…"
            minHeight={80}
            label="Thoughts to discuss later"
          />
        </div>
      )}

      {/* General notes — always visible at the bottom */}
      <div style={{ marginTop: 4, background: "#fafafa", border: "1.5px solid #ebebeb", borderRadius: 14, padding: "14px 16px" }}>
        <p style={{ fontSize: 13, fontWeight: 600, color: "#555", fontFamily: "'DM Sans', sans-serif", marginBottom: 4 }}>
          📝 My notes
        </p>
        <p style={{ fontSize: 12, color: "#aaa", fontFamily: "'DM Sans', sans-serif", marginBottom: 10, lineHeight: 1.5 }}>
          Anything you're thinking, feeling, or sensing that you want to remember.
        </p>
        <ExpandableTextarea
          value={answers.sp_notes || ""}
          onChange={v => set({ sp_notes: v })}
          placeholder="Jot down anything that's coming to mind — impressions, questions, things you've been sensing…"
          minHeight={75}
          label="My notes"
        />
      </div>
    </div>
  );
}

const EMOJI_OPTIONS = ["🙏","💍","🏘️","🏛️","📍","❤️","🌱","✝️","🎯","💼","🏡","👨‍👩‍👧","🌍","📖","💰","🕊️","⚓","🌟"];

function Vision({ step, answers, set, isSolo }) {
  const gate = answers.vi_gate;
  const anchors = answers.vi_anchors !== undefined ? answers.vi_anchors : null;
  const hasBuiltVision = anchors !== null;
  const [building, setBuilding] = useState(!hasBuiltVision);
  const [builderAnswers, setBuilderAnswers] = useState(answers.vi_builder || {});
  const [adding, setAdding] = useState(false);
  const [newText, setNewText] = useState("");
  const [newEmoji, setNewEmoji] = useState("🌱");
  const [editingIdx, setEditingIdx] = useState(null);
  const [generating, setGenerating] = useState(false);
  const currentAnchors = anchors || [];
  const builderHasContent = VISION_BUILDER_PROMPTS.some(p => (builderAnswers[p.id] || "").trim().length > 10);

  function updateAnchor(i, field, val) {
    set({ vi_anchors: currentAnchors.map((a, idx) => idx === i ? { ...a, [field]: val } : a) });
  }
  function removeAnchor(i) { set({ vi_anchors: currentAnchors.filter((_, idx) => idx !== i) }); }
  function addAnchor() {
    if (!newText.trim()) return;
    set({ vi_anchors: [...currentAnchors, { emoji: newEmoji, text: newText.trim() }] });
    setNewText(""); setNewEmoji("🌱"); setAdding(false);
  }

  async function generateAnchors() {
    setGenerating(true);
    try {
      const res = await fetch(`https://pjyfisrdgygidyztvjcz.supabase.co/functions/v1/generate-anchors`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${process.env.REACT_APP_SUPABASE_KEY}`,
        },
        body: JSON.stringify({
          years: builderAnswers.vb_years || "",
          important: builderAnswers.vb_important || "",
          success: builderAnswers.vb_success || "",
        }),
      });

      const data = await res.json();
      console.log("Anchor response:", data);
      const generated = Array.isArray(data.anchors) && data.anchors.length > 0
        ? data.anchors
        : [
            { emoji: "🙏", text: "Walk in faith through every decision" },
            { emoji: "👨‍👩‍👧", text: "Build a family rooted in love and purpose" },
            { emoji: "🌱", text: "Grow something that outlasts us" },
          ];

      set({ vi_anchors: generated, vi_builder: builderAnswers });
      // Small delay ensures state is committed before switching view
      setTimeout(() => {
        setBuilding(false);
        setGenerating(false);
      }, 100);
    } catch (e) {
      console.error("Anchor generation failed:", e);
      // Fallback anchors so the page is never empty
      set({
        vi_anchors: [
          { emoji: "🙏", text: "Walk in faith through every decision" },
          { emoji: "👨‍👩‍👧", text: "Build a family rooted in love and purpose" },
          { emoji: "🌱", text: "Grow something that outlasts us" },
        ],
        vi_builder: builderAnswers,
      });
      setTimeout(() => {
        setBuilding(false);
        setGenerating(false);
      }, 100);
    }
  }

  function skipToAnchors() {
    if (!hasBuiltVision) set({ vi_anchors: [] });
    setBuilding(false);
  }

  if (building) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <StepInfoBanner description={isSolo ? step.description_solo : step.description} />
        <div style={{ background: "#fafafa", borderRadius: 14, padding: "16px", border: "1.5px solid #e8e8e8" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
            <div>
              <p style={{ fontSize: 14, fontWeight: 700, color: "#111", fontFamily: "'DM Sans', sans-serif", marginBottom: 4 }}>Let's build your vision first.</p>
              <p style={{ fontSize: 13, color: "#888", fontFamily: "'DM Sans', sans-serif", lineHeight: 1.6, margin: 0 }}>
                Answer these to help define what you're building toward. Your answers will shape your vision anchors.
              </p>
            </div>
            <button
              onClick={skipToAnchors}
              style={{
                flexShrink: 0, background: "none", border: "1.5px solid #e0e0e0",
                borderRadius: 50, padding: "7px 14px", fontSize: 12,
                fontFamily: "'DM Sans', sans-serif", color: "#888",
                cursor: "pointer", outline: "none", whiteSpace: "nowrap",
              }}
            >
              Skip →
            </button>
          </div>
          <p style={{ fontSize: 11, color: "#bbb", fontFamily: "'DM Sans', sans-serif", marginTop: 8, marginBottom: 0, fontStyle: "italic" }}>
            Already have your vision dialed in? Tap Skip to go straight to your anchors.
          </p>
        </div>
        {VISION_BUILDER_PROMPTS.map(p => (
          <div key={p.id}>
            <p style={{ fontSize: 14, fontWeight: 600, color: "#111", fontFamily: "'DM Sans', sans-serif", marginBottom: 3 }}>{p.label}</p>
            <p style={{ fontSize: 12, color: "#aaa", fontFamily: "'DM Sans', sans-serif", marginBottom: 8, lineHeight: 1.5, fontStyle: "italic" }}>{p.hint}</p>
            <ExpandableTextarea
              value={builderAnswers[p.id] || ""}
              onChange={v => setBuilderAnswers(a => ({ ...a, [p.id]: v }))}
              placeholder={p.placeholder}
              minHeight={70}
              label={p.label}
            />
          </div>
        ))}
        <button
          onClick={generateAnchors}
          disabled={!builderHasContent || generating}
          style={{
            background: builderHasContent && !generating ? "#111" : "#eee",
            color: builderHasContent && !generating ? "#fff" : "#bbb",
            border: "none", borderRadius: 50, padding: "13px 24px",
            fontSize: 14, fontWeight: 600, fontFamily: "'DM Sans', sans-serif",
            cursor: builderHasContent && !generating ? "pointer" : "default", outline: "none",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
          }}>
          {generating
            ? <><span style={{ display: "inline-block", animation: "spin 1s linear infinite" }}>⚙️</span> Building your anchors…</>
            : "✨ Generate my vision anchors →"}
        </button>
        {builderHasContent && !generating && (
          <p style={{ fontSize: 11, color: "#bbb", fontFamily: "'DM Sans', sans-serif", textAlign: "center", margin: 0 }}>
            We'll suggest anchors based on what you wrote — you can edit or remove any of them
          </p>
        )}
        <style>{`@keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }`}</style>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <StepInfoBanner description={isSolo ? step.description_solo : step.description} />

      <div style={{ background: "#fafafa", borderRadius: 14, padding: "14px 16px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
          <p style={S.label}>Your vision anchors</p>
          <button onClick={() => setBuilding(true)} style={{ fontSize: 11, color: "#aaa", fontFamily: "'DM Sans', sans-serif", background: "none", border: "none", cursor: "pointer", outline: "none" }}>← Revisit builder</button>
        </div>

        {currentAnchors.length === 0 && (
          <p style={{ fontSize: 13, color: "#bbb", fontFamily: "'DM Sans', sans-serif", fontStyle: "italic", marginBottom: 10 }}>
            No anchors yet — add what matters most to you below.
          </p>
        )}

        {currentAnchors.map((a, i) => (
          <div key={i} style={{ borderBottom: i < currentAnchors.length - 1 ? "1px solid #ebebeb" : "none", padding: "6px 0" }}>
            {editingIdx === i ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 6, padding: "4px 0" }}>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                  {EMOJI_OPTIONS.map(e => (
                    <button key={e} onClick={() => updateAnchor(i, "emoji", e)} style={{ fontSize: 16, background: a.emoji === e ? "#111" : "#fff", border: `1.5px solid ${a.emoji === e ? "#111" : "#e0e0e0"}`, borderRadius: 8, padding: "4px 6px", cursor: "pointer", outline: "none" }}>{e}</button>
                  ))}
                </div>
                <input value={a.text} onChange={e => updateAnchor(i, "text", e.target.value)} style={{ ...S.input, width: "100%", boxSizing: "border-box", fontSize: 13 }} />
                <div style={{ display: "flex", gap: 6 }}>
                  <button onClick={() => setEditingIdx(null)} style={{ flex: 1, background: "#111", color: "#fff", border: "none", borderRadius: 8, padding: "8px", fontSize: 12, fontFamily: "'DM Sans', sans-serif", cursor: "pointer" }}>Done</button>
                  <button onClick={() => { removeAnchor(i); setEditingIdx(null); }} style={{ background: "none", border: "1.5px solid #e0e0e0", borderRadius: 8, padding: "8px 12px", fontSize: 12, fontFamily: "'DM Sans', sans-serif", color: "#c0392b", cursor: "pointer" }}>Remove</button>
                </div>
              </div>
            ) : (
              <button onClick={() => setEditingIdx(i)} style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", background: "none", border: "none", cursor: "pointer", padding: "6px 0", textAlign: "left" }}>
                <span style={{ fontSize: 18 }}>{a.emoji}</span>
                <span style={{ fontSize: 13, color: "#333", fontFamily: "'DM Sans', sans-serif", lineHeight: 1.4, flex: 1 }}>{a.text}</span>
                <span style={{ fontSize: 11, color: "#ccc" }}>✏️</span>
              </button>
            )}
          </div>
        ))}

        {adding ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 10, paddingTop: 10, borderTop: "1px solid #ebebeb" }}>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
              {EMOJI_OPTIONS.map(e => (
                <button key={e} onClick={() => setNewEmoji(e)} style={{ fontSize: 16, background: newEmoji === e ? "#111" : "#fff", border: `1.5px solid ${newEmoji === e ? "#111" : "#e0e0e0"}`, borderRadius: 8, padding: "4px 6px", cursor: "pointer", outline: "none" }}>{e}</button>
              ))}
            </div>
            <input value={newText} onChange={e => setNewText(e.target.value)} placeholder="Describe this vision anchor…" style={{ ...S.input, width: "100%", boxSizing: "border-box", fontSize: 13 }} />
            <div style={{ display: "flex", gap: 6 }}>
              <button onClick={addAnchor} style={{ flex: 1, background: "#111", color: "#fff", border: "none", borderRadius: 8, padding: "8px", fontSize: 12, fontFamily: "'DM Sans', sans-serif", cursor: "pointer" }}>Add anchor</button>
              <button onClick={() => setAdding(false)} style={{ background: "none", border: "1.5px solid #e0e0e0", borderRadius: 8, padding: "8px 12px", fontSize: 12, fontFamily: "'DM Sans', sans-serif", color: "#888", cursor: "pointer" }}>Cancel</button>
            </div>
          </div>
        ) : (
          <button onClick={() => setAdding(true)} style={{ marginTop: 10, width: "100%", background: "none", border: "1.5px dashed #ddd", borderRadius: 10, padding: "9px", fontSize: 12, fontFamily: "'DM Sans', sans-serif", color: "#aaa", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 5 }}>
            + Add your own
          </button>
        )}
      </div>

      <div>
        <p style={{ ...S.label, marginBottom: 8 }}>{isSolo ? step.gate_question_solo : step.gate_question}</p>
        <GateButtons value={gate} onChange={v => set({ vi_gate: v })} yesLabel="✓ Yes it does" noLabel="No / Not sure" />
        {gate && <GateResult pass={gate === "yes"} passMsg={step.gate_pass} failMsg={step.gate_fail} />}
      </div>
    </div>
  );
}

function TagInput({ tags = [], onChange, placeholder, color }) {
  const [draft, setDraft] = useState("");

  function addTag() {
    const trimmed = draft.trim();
    if (!trimmed) return;
    onChange([...tags, trimmed]);
    setDraft("");
  }

  function removeTag(i) {
    onChange(tags.filter((_, idx) => idx !== i));
  }

  function handleKey(e) {
    if (e.key === "Enter") { e.preventDefault(); addTag(); }
  }

  const isPro = color === "pro";
  const tagBg = isPro ? "#e8f5e9" : "#fff3e0";
  const tagBorder = isPro ? "#81c784" : "#ffb74d";
  const tagColor = isPro ? "#2e7d32" : "#e65100";
  const xBg = isPro ? "#c8e6c9" : "#ffe0b2";
  const xColor = isPro ? "#388e3c" : "#bf360c";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {tags.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {tags.map((tag, i) => (
            <div key={i} style={{
              display: "flex", alignItems: "center", gap: 5,
              background: tagBg, border: `1.5px solid ${tagBorder}`,
              borderRadius: 50, padding: "5px 8px 5px 12px",
            }}>
              <span style={{ fontSize: 12, fontFamily: "'DM Sans', sans-serif", color: tagColor, fontWeight: 600, lineHeight: 1.4 }}>{tag}</span>
              <button onClick={() => removeTag(i)} style={{
                background: xBg, border: "none", borderRadius: "50%",
                width: 17, height: 17, cursor: "pointer", outline: "none",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 9, color: xColor, flexShrink: 0, lineHeight: 1, padding: 0, fontWeight: 700,
              }}>✕</button>
            </div>
          ))}
        </div>
      )}
      <div style={{ display: "flex", gap: 6 }}>
        <input
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={handleKey}
          placeholder={placeholder}
          style={{ ...S.input, flex: 1, fontSize: 13, padding: "9px 12px" }}
        />
        <button onClick={addTag} style={{
          background: draft.trim() ? (isPro ? "#2e7d32" : "#e65100") : "#eee",
          color: draft.trim() ? "#fff" : "#bbb",
          border: "none", borderRadius: 10, padding: "9px 11px",
          fontSize: 16, fontWeight: 700, fontFamily: "'DM Sans', sans-serif",
          cursor: draft.trim() ? "pointer" : "default", outline: "none",
          transition: "all 0.15s", flexShrink: 0, lineHeight: 1,
          minWidth: 36,
        }}>+</button>
      </div>
      <p style={{ fontSize: 11, color: "#ccc", fontFamily: "'DM Sans', sans-serif", margin: 0 }}>
        Press Enter or tap Add
      </p>
    </div>
  );
}

function OptionCard({ optionKey, optionLabel, answers, set, placeholder, onRemove }) {
  const name = answers[optionKey] || "";
  const [editing, setEditing] = useState(!name);
  const prosKey = `tags_pros_${optionKey.slice(-1)}`;
  const consKey = `tags_cons_${optionKey.slice(-1)}`;

  return (
    <div style={{ border: "1.5px solid #e0e0e0", borderRadius: 16, overflow: "hidden", background: "#fff" }}>

      {/* Option header */}
      {name && !editing ? (
        <div style={{ background: "#111", padding: "14px 18px", display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <p style={{ fontSize: 10, color: "#888", fontFamily: "'DM Sans', sans-serif", letterSpacing: "0.08em", textTransform: "uppercase", margin: 0, marginBottom: 3 }}>{optionLabel}</p>
            <p style={{ fontSize: 16, fontWeight: 700, color: "#fff", fontFamily: "'Playfair Display', serif", margin: 0, lineHeight: 1.4 }}>{name}</p>
          </div>
          <div style={{ display: "flex", gap: 6, flexShrink: 0, marginLeft: 12 }}>
            <button onClick={() => setEditing(true)} style={{ background: "rgba(255,255,255,0.12)", border: "none", borderRadius: 8, padding: "5px 10px", fontSize: 11, fontFamily: "'DM Sans', sans-serif", color: "#ccc", cursor: "pointer", outline: "none" }}>Edit</button>
            {onRemove && <button onClick={onRemove} style={{ background: "rgba(255,255,255,0.08)", border: "none", borderRadius: 8, padding: "5px 10px", fontSize: 11, fontFamily: "'DM Sans', sans-serif", color: "#888", cursor: "pointer", outline: "none" }}>Remove</button>}
          </div>
        </div>
      ) : (
        <div style={{ background: "#f7f7f7", padding: "14px 16px", borderBottom: "1.5px solid #e0e0e0" }}>
          <p style={{ ...S.label, marginBottom: 8 }}>{optionLabel}</p>
          <textarea
            value={name}
            onChange={e => set({ [optionKey]: e.target.value })}
            placeholder={placeholder}
            autoFocus={!name}
            rows={2}
            style={{ ...S.input, width: "100%", boxSizing: "border-box", resize: "none", fontSize: 14, lineHeight: 1.5 }}
          />
          {name && (
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <button onClick={() => setEditing(false)} style={{ background: "#111", color: "#fff", border: "none", borderRadius: 8, padding: "7px 16px", fontSize: 12, fontFamily: "'DM Sans', sans-serif", cursor: "pointer", outline: "none" }}>Done ✓</button>
              {onRemove && <button onClick={onRemove} style={{ background: "none", border: "1.5px solid #e0e0e0", borderRadius: 8, padding: "7px 14px", fontSize: 12, fontFamily: "'DM Sans', sans-serif", color: "#888", cursor: "pointer", outline: "none" }}>Remove option</button>}
            </div>
          )}
        </div>
      )}

      {/* Pros & cons — side by side inside the card */}
      {name && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 0 }}>
          <div style={{ padding: "14px 16px", borderRight: "1px solid #f0f0f0" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10 }}>
              <span style={{ fontSize: 12 }}>✅</span>
              <p style={{ fontSize: 11, fontWeight: 700, color: "#2e7d32", fontFamily: "'DM Sans', sans-serif", margin: 0, letterSpacing: "0.06em", textTransform: "uppercase" }}>Pros</p>
            </div>
            <TagInput
              tags={answers[prosKey] || []}
              onChange={tags => set({ [prosKey]: tags })}
              placeholder="What's genuinely good?"
              color="pro"
            />
          </div>
          <div style={{ padding: "14px 16px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10 }}>
              <span style={{ fontSize: 12 }}>⚠️</span>
              <p style={{ fontSize: 11, fontWeight: 700, color: "#e65100", fontFamily: "'DM Sans', sans-serif", margin: 0, letterSpacing: "0.06em", textTransform: "uppercase" }}>Cons</p>
            </div>
            <TagInput
              tags={answers[consKey] || []}
              onChange={tags => set({ [consKey]: tags })}
              placeholder="Real costs & pain…"
              color="con"
            />
          </div>
        </div>
      )}
    </div>
  );
}

function Reality({ answers, set, isSolo }) {
  const [showThird, setShowThird] = useState(!!(answers.opt_c));
  const [currentOption, setCurrentOption] = useState(0); // 0=A, 1=B, 2=C

  const optKeys = showThird ? ["opt_a", "opt_b", "opt_c"] : ["opt_a", "opt_b"];
  const optLabels = ["Option A", "Option B", "Option C"];
  const optPlaceholders = ["e.g. Stay in Denver", "e.g. Move to NW Arkansas", "e.g. Stay put for now"];

  const currentKey = optKeys[currentOption];
  const currentName = answers[currentKey] || "";
  const isLastOption = currentOption === optKeys.length - 1;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <StepInfoBanner description={isSolo ? STEPS[2].description_solo : STEPS[2].description} />

      {/* Option tab navigation */}
      <div style={{ display: "flex", gap: 6 }}>
        {optKeys.map((k, i) => (
          <button key={k} onClick={() => setCurrentOption(i)} style={{
            flex: 1, padding: "8px 4px", borderRadius: 10, fontSize: 12, fontWeight: 600,
            fontFamily: "'DM Sans', sans-serif", cursor: "pointer", outline: "none",
            border: `1.5px solid ${currentOption === i ? "#111" : "#e0e0e0"}`,
            background: currentOption === i ? "#111" : (answers[k] || "").length > 1 ? "#f7f7f7" : "#fafafa",
            color: currentOption === i ? "#fff" : (answers[k] || "").length > 1 ? "#555" : "#bbb",
            transition: "all 0.15s",
          }}>
            {optLabels[i]}
            {(answers[k] || "").length > 1 && currentOption !== i && <span style={{ marginLeft: 4 }}>✓</span>}
          </button>
        ))}
        {!showThird && (
          <button onClick={() => { setShowThird(true); setCurrentOption(2); }} style={{
            padding: "8px 10px", borderRadius: 10, fontSize: 12,
            fontFamily: "'DM Sans', sans-serif", cursor: "pointer", outline: "none",
            border: "1.5px dashed #ddd", background: "#fafafa", color: "#aaa",
          }}>+ C</button>
        )}
      </div>

      {/* Current option card */}
      <OptionCard
        key={currentKey}
        optionKey={currentKey}
        optionLabel={optLabels[currentOption]}
        answers={answers}
        set={set}
        placeholder={optPlaceholders[currentOption]}
        onRemove={currentOption === 2 ? () => { setShowThird(false); setCurrentOption(1); set({ opt_c: "", tags_pros_c: [], tags_cons_c: [] }); } : null}
      />

      {/* Next option / done navigation */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        {currentOption > 0 ? (
          <button onClick={() => setCurrentOption(c => c - 1)} style={{ background: "none", border: "none", fontSize: 13, fontFamily: "'DM Sans', sans-serif", color: "#888", cursor: "pointer", outline: "none" }}>← Previous</button>
        ) : <div />}
        {!isLastOption && (
          <button
            onClick={() => currentName.length > 1 && setCurrentOption(c => c + 1)}
            style={{ background: currentName.length > 1 ? "#111" : "#eee", color: currentName.length > 1 ? "#fff" : "#bbb", border: "none", borderRadius: 50, padding: "10px 22px", fontSize: 13, fontWeight: 600, fontFamily: "'DM Sans', sans-serif", cursor: currentName.length > 1 ? "pointer" : "default", outline: "none" }}>
            Next option →
          </button>
        )}
        {isLastOption && currentName.length > 1 && (
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ width: 7, height: 7, borderRadius: "50%", background: "#4caf50", display: "inline-block" }} />
            <span style={{ fontSize: 12, color: "#4caf50", fontFamily: "'DM Sans', sans-serif" }}>All options named — continue when ready</span>
          </div>
        )}
      </div>

      <div style={{ background: "#111", borderRadius: 12, padding: "13px 16px", display: "flex", gap: 10 }}>
        <span>👉</span>
        <p style={{ fontSize: 13, color: "#ccc", fontFamily: "'DM Sans', sans-serif", margin: 0, lineHeight: 1.5 }}>
          If someone is trying to eliminate all pain, they'll stay stuck forever. Choose the pain worth paying.
        </p>
      </div>
    </div>
  );
}

function Emotional({ answers, set, isSolo }) {
  const fears = answers.fear_sel || {};
  const categorized = answers.fear_categorized || {};
  const customFears = answers.fear_custom || []; // [{ id, label }]
  const extra = answers.fear_extra || "";
  const [draft, setDraft] = useState("");

  // All selected fear IDs = preset ones toggled on + all custom ones
  const selectedPresetIds = Object.keys(fears).filter(k => fears[k]);
  const allSelectedIds = [
    ...selectedPresetIds,
    ...customFears.map(f => f.id),
  ];

  function togglePreset(id) {
    const next = { ...fears, [id]: !fears[id] };
    if (fears[id]) {
      const nextCat = { ...categorized };
      delete nextCat[id];
      set({ fear_sel: next, fear_categorized: nextCat });
    } else {
      set({ fear_sel: next });
    }
  }

  function addCustom() {
    const trimmed = draft.trim();
    if (!trimmed) return;
    const id = `custom_${Date.now()}`;
    set({ fear_custom: [...customFears, { id, label: trimmed }] });
    setDraft("");
  }

  function removeCustom(id) {
    const nextCat = { ...categorized };
    delete nextCat[id];
    set({
      fear_custom: customFears.filter(f => f.id !== id),
      fear_categorized: nextCat,
    });
  }

  function categorize(id, bucket) {
    if (categorized[id] === bucket) {
      const next = { ...categorized }; delete next[id];
      set({ fear_categorized: next });
    } else {
      set({ fear_categorized: { ...categorized, [id]: bucket } });
    }
  }

  function getLabelFor(id) {
    const preset = FEAR_OPTIONS.find(o => o.id === id);
    if (preset) return `${preset.emoji} ${preset.label}`;
    const custom = customFears.find(f => f.id === id);
    return custom ? `✏️ ${custom.label}` : id;
  }

  const validIds = allSelectedIds.filter(id => categorized[id] === "valid");
  const fearBasedIds = allSelectedIds.filter(id => categorized[id] === "fear_based");
  const uncategorizedIds = allSelectedIds.filter(id => !categorized[id]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <StepInfoBanner description={isSolo ? STEPS[3].description_solo : STEPS[3].description} />

      {/* Dual reminders */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
        <div style={{ background: "#f0f4ff", border: "1.5px solid #c5cff5", borderRadius: 12, padding: "12px 14px" }}>
          <p style={{ fontSize: 11, fontWeight: 700, color: "#3949ab", fontFamily: "'DM Sans', sans-serif", letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 5 }}>🧠 Logic-first person</p>
          <p style={{ fontSize: 12, color: "#444", fontFamily: "'DM Sans', sans-serif", margin: 0, lineHeight: 1.6 }}>
            Your job right now isn't to solve or rebut. It's to understand. Feelings don't need to be logical to be real.
          </p>
        </div>
        <div style={{ background: "#fff4f0", border: "1.5px solid #f5c5b0", borderRadius: 12, padding: "12px 14px" }}>
          <p style={{ fontSize: 11, fontWeight: 700, color: "#bf360c", fontFamily: "'DM Sans', sans-serif", letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 5 }}>❤️ Emotion-first person</p>
          <p style={{ fontSize: 12, color: "#444", fontFamily: "'DM Sans', sans-serif", margin: 0, lineHeight: 1.6 }}>
            This is your space. Say what's actually true — not what you think you're supposed to feel.
          </p>
        </div>
      </div>

      {/* Preset fear selection */}
      <div>
        <p style={{ ...S.label, marginBottom: 8 }}>What's honestly on the table? Select everything that's present.</p>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
          {FEAR_OPTIONS.map(f => {
            const sel = !!fears[f.id];
            const cat = categorized[f.id];
            const borderColor = cat === "valid" ? "#81c784" : cat === "fear_based" ? "#ffb74d" : sel ? "#111" : "#e0e0e0";
            const bg = cat === "valid" ? "#e8f5e9" : cat === "fear_based" ? "#fff8f1" : sel ? "#111" : "#fafafa";
            const color = cat === "valid" ? "#2e7d32" : cat === "fear_based" ? "#e65100" : sel ? "#fff" : "#444";
            return (
              <button key={f.id} onClick={() => togglePreset(f.id)} style={{
                border: `1.5px solid ${borderColor}`, borderRadius: 50, padding: "7px 14px",
                background: bg, color, fontSize: 12, fontFamily: "'DM Sans', sans-serif",
                cursor: "pointer", outline: "none",
                display: "flex", alignItems: "center", gap: 5, transition: "all 0.15s",
              }}><span>{f.emoji}</span>{f.label}</button>
            );
          })}
        </div>
      </div>

      {/* Custom fear entry */}
      <div style={{ background: "#fafafa", border: "1.5px solid #e8e8e8", borderRadius: 12, padding: "13px 14px" }}>
        <p style={{ ...S.label, marginBottom: 8 }}>Something not on the list? Add it here.</p>

        {/* Existing custom tags */}
        {customFears.length > 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 10 }}>
            {customFears.map(f => {
              const cat = categorized[f.id];
              const bg = cat === "valid" ? "#e8f5e9" : cat === "fear_based" ? "#fff8f1" : "#efefef";
              const border = cat === "valid" ? "#81c784" : cat === "fear_based" ? "#ffb74d" : "#ddd";
              const color = cat === "valid" ? "#2e7d32" : cat === "fear_based" ? "#e65100" : "#444";
              return (
                <div key={f.id} style={{ display: "flex", alignItems: "center", gap: 5, background: bg, border: `1.5px solid ${border}`, borderRadius: 50, padding: "5px 8px 5px 12px" }}>
                  <span style={{ fontSize: 12, color, fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}>✏️ {f.label}</span>
                  <button onClick={() => removeCustom(f.id)} style={{ background: "#e0e0e0", border: "none", borderRadius: "50%", width: 16, height: 16, cursor: "pointer", outline: "none", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, color: "#888", padding: 0, flexShrink: 0 }}>✕</button>
                </div>
              );
            })}
          </div>
        )}

        {/* Input row */}
        <div style={{ display: "flex", gap: 6 }}>
          <input
            value={draft}
            onChange={e => setDraft(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addCustom(); } }}
            placeholder="e.g. I'm afraid we'll grow apart through this…"
            style={{ ...S.input, flex: 1, fontSize: 13, padding: "9px 12px" }}
          />
          <button onClick={addCustom} style={{
            background: draft.trim() ? "#111" : "#eee",
            color: draft.trim() ? "#fff" : "#bbb",
            border: "none", borderRadius: 10, padding: "9px 11px",
            fontSize: 16, fontWeight: 700, fontFamily: "'DM Sans', sans-serif",
            cursor: draft.trim() ? "pointer" : "default", outline: "none", flexShrink: 0,
            lineHeight: 1, minWidth: 36,
          }}>+</button>
        </div>
        <p style={{ fontSize: 11, color: "#ccc", fontFamily: "'DM Sans', sans-serif", margin: "5px 0 0" }}>Press Enter or tap Add</p>
      </div>

      {/* Sorting tray + buckets — appears once anything is selected */}
      {allSelectedIds.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <p style={{ ...S.label, marginBottom: 2 }}>Now sort each one — is it a real concern or a fear to surrender?</p>
          <p style={{ fontSize: 12, color: "#aaa", fontFamily: "'DM Sans', sans-serif", margin: 0 }}>
            Valid concerns need a plan. Fear-based ones need surrender — not a solution.
          </p>

          {/* Unsorted tray */}
          {uncategorizedIds.length > 0 && (
            <div style={{ background: "#fafafa", border: "1.5px dashed #ddd", borderRadius: 12, padding: "12px 14px" }}>
              <p style={{ fontSize: 11, color: "#bbb", fontFamily: "'DM Sans', sans-serif", marginBottom: 10, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase" }}>Not yet sorted</p>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {uncategorizedIds.map(id => (
                  <div key={id} style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    <span style={{ fontSize: 12, color: "#555", fontFamily: "'DM Sans', sans-serif", padding: "5px 12px", background: "#efefef", borderRadius: 50 }}>
                      {getLabelFor(id)}
                    </span>
                    <div style={{ display: "flex", gap: 4 }}>
                      <button onClick={() => categorize(id, "valid")} style={{ flex: 1, fontSize: 10, fontFamily: "'DM Sans', sans-serif", background: "#e8f5e9", color: "#2e7d32", border: "1px solid #a5d6a7", borderRadius: 6, padding: "4px 6px", cursor: "pointer", outline: "none", fontWeight: 600 }}>✅ Valid</button>
                      <button onClick={() => categorize(id, "fear_based")} style={{ flex: 1, fontSize: 10, fontFamily: "'DM Sans', sans-serif", background: "#fff8f1", color: "#e65100", border: "1px solid #ffcc80", borderRadius: 6, padding: "4px 6px", cursor: "pointer", outline: "none", fontWeight: 600 }}>🙏 Fear</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Valid / Fear-based buckets */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            <div style={{ background: "#e8f5e9", border: "1.5px solid #a5d6a7", borderRadius: 12, padding: "12px 14px", minHeight: 80 }}>
              <p style={{ fontSize: 11, fontWeight: 700, color: "#2e7d32", fontFamily: "'DM Sans', sans-serif", marginBottom: 4, letterSpacing: "0.06em", textTransform: "uppercase" }}>✅ Valid Concerns</p>
              <p style={{ fontSize: 11, color: "#4caf50", fontFamily: "'DM Sans', sans-serif", marginBottom: 8 }}>These need a real plan.</p>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                {validIds.length === 0 && <span style={{ fontSize: 12, color: "#a5d6a7", fontFamily: "'DM Sans', sans-serif" }}>None yet</span>}
                {validIds.map(id => (
                  <button key={id} onClick={() => categorize(id, "valid")} style={{ fontSize: 11, fontFamily: "'DM Sans', sans-serif", background: "#fff", color: "#2e7d32", border: "1.5px solid #81c784", borderRadius: 50, padding: "4px 10px", cursor: "pointer", outline: "none", display: "flex", alignItems: "center", gap: 4 }}>
                    {getLabelFor(id)} <span style={{ fontSize: 9, color: "#aaa" }}>✕</span>
                  </button>
                ))}
              </div>
            </div>
            <div style={{ background: "#fff8f1", border: "1.5px solid #ffcc80", borderRadius: 12, padding: "12px 14px", minHeight: 80 }}>
              <p style={{ fontSize: 11, fontWeight: 700, color: "#e65100", fontFamily: "'DM Sans', sans-serif", marginBottom: 4, letterSpacing: "0.06em", textTransform: "uppercase" }}>🙏 Fear-Based</p>
              <p style={{ fontSize: 11, color: "#ffb74d", fontFamily: "'DM Sans', sans-serif", marginBottom: 8 }}>These need surrender, not a solution.</p>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                {fearBasedIds.length === 0 && <span style={{ fontSize: 12, color: "#ffcc80", fontFamily: "'DM Sans', sans-serif" }}>None yet</span>}
                {fearBasedIds.map(id => (
                  <button key={id} onClick={() => categorize(id, "fear_based")} style={{ fontSize: 11, fontFamily: "'DM Sans', sans-serif", background: "#fff", color: "#e65100", border: "1.5px solid #ffb74d", borderRadius: 50, padding: "4px 10px", cursor: "pointer", outline: "none", display: "flex", alignItems: "center", gap: 4 }}>
                    {getLabelFor(id)} <span style={{ fontSize: 9, color: "#aaa" }}>✕</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Free-form notes */}
      <div>
        <p style={{ ...S.label, marginBottom: 6 }}>Anything else that needs to be said?</p>
        <ExpandableTextarea
          value={extra}
          onChange={v => set({ fear_extra: v })}
          placeholder="Write it out — even if it feels messy or hard to explain…"
          minHeight={75}
          label="Additional thoughts"
        />
      </div>
    </div>
  );
}

function Trust({ step, answers, set, isSolo }) {
  const prompts = isSolo ? TRUST_PROMPTS_SOLO : TRUST_PROMPTS_COUPLES;
  const responses = answers.tr_responses || {};
  const notes = answers.tr_notes || {};
  const [currentIdx, setCurrentIdx] = useState(0);
  const allAnswered = prompts.every(p => responses[p.id] === "yes" || responses[p.id] === "no");
  const p = prompts[currentIdx];
  const val = responses[p.id];
  const isLast = currentIdx === prompts.length - 1;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <StepInfoBanner description={isSolo ? step.description_solo : step.description} />

      <div style={{ background: "#fafafa", border: "1.5px solid #ebebeb", borderRadius: 12, padding: "11px 14px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span style={{ fontSize: 12, color: "#aaa", fontFamily: "'DM Sans', sans-serif" }}>Question {currentIdx + 1} of {prompts.length}</span>
        <div style={{ display: "flex", gap: 4 }}>
          {prompts.map((_, i) => (
            <div key={i} style={{ width: i === currentIdx ? 16 : 6, height: 6, borderRadius: 3, background: responses[prompts[i].id] ? "#4caf50" : i === currentIdx ? "#111" : "#e0e0e0", transition: "all 0.2s" }} />
          ))}
        </div>
      </div>

      {/* Current question card */}
      <div key={p.id} style={{ background: "#fafafa", border: `1.5px solid ${val === "yes" ? "#a5d6a7" : val === "no" ? "#ffcc80" : "#ebebeb"}`, borderRadius: 16, padding: "18px 16px", animation: "fadeUp 0.25s ease", transition: "border-color 0.2s" }}>
        <p style={{ fontSize: 16, color: "#111", fontFamily: "'DM Sans', sans-serif", fontWeight: 700, margin: 0, marginBottom: 14, lineHeight: 1.5 }}>
          {p.question}
        </p>
        <div style={{ display: "flex", gap: 8 }}>
          {[["yes", "✓ Yes — I'm there"], ["no", "○ Not fully yet"]].map(([v, lbl]) => (
            <button key={v} onClick={() => set({ tr_responses: { ...responses, [p.id]: v } })} style={{
              flex: 1, padding: "11px 12px", borderRadius: 12,
              border: `1.5px solid ${val === v ? (v === "yes" ? "#81c784" : "#ffb74d") : "#e0e0e0"}`,
              background: val === v ? (v === "yes" ? "#e8f5e9" : "#fff8f1") : "#fff",
              color: val === v ? (v === "yes" ? "#2e7d32" : "#e65100") : "#777",
              fontSize: 14, fontWeight: 600, fontFamily: "'DM Sans', sans-serif",
              cursor: "pointer", transition: "all 0.15s", outline: "none",
            }}>{lbl}</button>
          ))}
        </div>
        {p.followup && (
          <div style={{ marginTop: 12 }}>
            <ExpandableTextarea
              value={notes[p.followup.id] || ""}
              onChange={v => set({ tr_notes: { ...notes, [p.followup.id]: v } })}
              placeholder={p.followup.placeholder}
              minHeight={60}
              label={p.followup.placeholder}
            />
          </div>
        )}
      </div>

      {/* Navigation */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
        {currentIdx > 0 ? (
          <button onClick={() => setCurrentIdx(i => i - 1)} style={{ background: "none", border: "none", fontSize: 13, fontFamily: "'DM Sans', sans-serif", color: "#888", cursor: "pointer", outline: "none" }}>← Back</button>
        ) : <div />}

        {!isLast ? (
          <button
            onClick={() => val && setCurrentIdx(i => i + 1)}
            style={{
              background: val ? "#111" : "#eee", color: val ? "#fff" : "#bbb",
              border: "none", borderRadius: 50, padding: "13px 28px",
              fontSize: 15, fontWeight: 700, fontFamily: "'DM Sans', sans-serif",
              cursor: val ? "pointer" : "default", outline: "none", transition: "all 0.2s",
              display: "flex", alignItems: "center", gap: 8,
            }}>
            Next <span style={{ fontSize: 18 }}>→</span>
          </button>
        ) : val && (
          <div style={{ background: "#111", borderRadius: 14, padding: "14px 18px", flex: 1 }}>
            <p style={{ fontSize: 14, fontWeight: 700, color: "#fff", fontFamily: "'DM Sans', sans-serif", margin: 0, marginBottom: 4 }}>
              {Object.values(responses).every(v => v === "yes") ? "🙏 You're aligned and ready." : "You're being honest — that's the right move."}
            </p>
            <p style={{ fontSize: 13, color: "#aaa", fontFamily: "'DM Sans', sans-serif", margin: 0, lineHeight: 1.5 }}>
              {Object.values(responses).every(v => v === "yes")
                ? "That kind of unity doesn't happen by accident. Move forward with confidence."
                : "The places you answered 'not fully yet' aren't failures — they're your next conversation. Talk through them before deciding."}
            </p>
          </div>
        )}
      </div>

      {/* Overview of all answers once done */}
      {allAnswered && (
        <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 4 }}>
          <p style={{ ...S.label, marginBottom: 4 }}>Your answers</p>
          {prompts.map((q, i) => (
            <button key={q.id} onClick={() => setCurrentIdx(i)} style={{
              display: "flex", justifyContent: "space-between", alignItems: "center",
              background: "#fafafa", border: "1.5px solid #ebebeb", borderRadius: 10,
              padding: "9px 12px", cursor: "pointer", outline: "none", textAlign: "left",
            }}>
              <span style={{ fontSize: 12, color: "#555", fontFamily: "'DM Sans', sans-serif", lineHeight: 1.4, flex: 1, marginRight: 8 }}>{q.question}</span>
              <span style={{ fontSize: 12, fontWeight: 600, fontFamily: "'DM Sans', sans-serif", flexShrink: 0, color: responses[q.id] === "yes" ? "#2e7d32" : "#e65100" }}>
                {responses[q.id] === "yes" ? "✓ Yes" : "○ Not yet"}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const READINESS_PROMPTS = [
  { id: "enough_info", text: "I have enough information to make this call — even if it's not perfect." },
  { id: "not_circling", text: "I'm not still circling because of fear. If something feels unresolved, it's specific and nameable." },
  { id: "heard", text: "I feel like my perspective has been genuinely heard in this process." },
  { id: "both_costs", text: "I understand what both options will cost — not just the one I prefer." },
];

function Threshold({ step, answers, set, isSolo }) {
  const readiness = answers.th_readiness || {};
  const decisionText = answers.th_decision_text || "";
  const deadline = answers.th_deadline || "";
  const deadlineAck = answers.th_deadline_ack || false;
  const allReady = READINESS_PROMPTS.every(p => readiness[p.id] === true);
  const fullyDone = allReady && decisionText.trim().length > 3 && deadline.trim().length > 3 && deadlineAck;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      <p style={S.body}>{step.description}</p>

      {/* Part 1 — Readiness check */}
      <div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
          <div style={{ width: 22, height: 22, borderRadius: "50%", background: C.teal, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <span style={{ fontSize: 11, color: "#fff", fontWeight: 700, fontFamily: "'DM Sans', sans-serif" }}>1</span>
          </div>
          <p style={{ fontSize: 14, fontWeight: 700, color: C.tealDark, fontFamily: "'DM Sans', sans-serif", margin: 0 }}>Am I ready to decide?</p>
        </div>
        <p style={{ fontSize: 13, color: C.charcoalMid, fontFamily: "'DM Sans', sans-serif", marginBottom: 10, lineHeight: 1.6 }}>
          Answer these for yourself. Sitting with discomfort is okay — but sitting with avoidance is different.
        </p>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {READINESS_PROMPTS.map(p => (
            <CheckRow
              key={p.id}
              checked={!!readiness[p.id]}
              text={p.text}
              onToggle={() => set({ th_readiness: { ...readiness, [p.id]: !readiness[p.id] } })}
            />
          ))}
        </div>
        {!allReady && Object.keys(readiness).length > 0 && (
          <div style={{ marginTop: 10, background: C.goldBg, border: `1.5px solid ${C.goldLight}`, borderRadius: 12, padding: "12px 14px" }}>
            <p style={{ fontSize: 13, color: C.goldDark, fontFamily: "'DM Sans', sans-serif", margin: 0, lineHeight: 1.6 }}>
              {isSolo ? "The unchecked items aren't blockers — they're things to sit with. What specifically is still unresolved?" : "The unchecked items above aren't blockers — they're conversation starters. What specifically is still unresolved?"}
            </p>
            <ExpandableTextarea
              value={answers.th_unresolved || ""}
              onChange={v => set({ th_unresolved: v })}
              placeholder="Name it clearly — vague anxiety is harder to work through than a specific concern…"
              minHeight={70}
              label="What's still unresolved?"
            />
          </div>
        )}
      </div>

      {/* Part 2 — Define the decision */}
      <div style={{ borderTop: `1px solid ${C.tealBg}`, paddingTop: 18 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
          <div style={{ width: 22, height: 22, borderRadius: "50%", background: C.teal, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <span style={{ fontSize: 11, color: "#fff", fontWeight: 700, fontFamily: "'DM Sans', sans-serif" }}>2</span>
          </div>
          <p style={{ fontSize: 14, fontWeight: 700, color: C.tealDark, fontFamily: "'DM Sans', sans-serif", margin: 0 }}>Write out exactly what you're deciding.</p>
        </div>
        <p style={{ fontSize: 13, color: C.charcoalMid, fontFamily: "'DM Sans', sans-serif", marginBottom: 10, lineHeight: 1.6 }}>
          Not the options — the decision. In plain language. If you can't write it clearly, you're not ready to make it.
        </p>
        <ExpandableTextarea
          value={decisionText}
          onChange={v => set({ th_decision_text: v })}
          placeholder={isSolo ? "e.g. I am deciding to accept the job offer in Austin by Friday…" : "e.g. We are deciding whether to sell our home and relocate to Northwest Arkansas by end of summer…"}
          minHeight={90}
          label="Write out your decision"
        />
        {decisionText.trim().length > 3 && (
          <div style={{ marginTop: 8, background: C.tealBg, borderRadius: 10, padding: "10px 14px", border: `1.5px solid ${C.tealPale}` }}>
            <p style={{ fontSize: 11, color: C.teal, fontFamily: "'DM Sans', sans-serif", margin: 0, marginBottom: 4, letterSpacing: "0.06em", textTransform: "uppercase" }}>What you're deciding</p>
            <p style={{ fontSize: 14, color: C.tealDark, fontFamily: "'Playfair Display', serif", fontWeight: 600, margin: 0, lineHeight: 1.5 }}>"{decisionText}"</p>
          </div>
        )}
      </div>

      {/* Part 3 — Deadline */}
      <div style={{ borderTop: `1px solid ${C.tealBg}`, paddingTop: 18 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
          <div style={{ width: 22, height: 22, borderRadius: "50%", background: C.teal, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <span style={{ fontSize: 11, color: "#fff", fontWeight: 700, fontFamily: "'DM Sans', sans-serif" }}>3</span>
          </div>
          <p style={{ fontSize: 14, fontWeight: 700, color: C.tealDark, fontFamily: "'DM Sans', sans-serif", margin: 0 }}>{isSolo ? "Set a deadline." : "Set a deadline — together."}</p>
        </div>
        <p style={{ fontSize: 13, color: C.charcoalMid, fontFamily: "'DM Sans', sans-serif", marginBottom: 10, lineHeight: 1.6 }}>
          {isSolo ? "Open-ended decisions don't close. Put a date on it and honor it." : "Open-ended decisions don't close. Put a date on it and honor it as a team."}
        </p>
        <input
          value={deadline}
          onChange={e => set({ th_deadline: e.target.value })}
          placeholder={isSolo ? "e.g. I will make this decision by Friday, April 27th" : "e.g. We will make this decision by Sunday, April 27th"}
          style={{ ...S.input, width: "100%", boxSizing: "border-box", fontSize: 14 }}
        />
        {deadline.trim().length > 3 && (
          <button
            onClick={() => set({ th_deadline_ack: !deadlineAck })}
            style={{
              marginTop: 10, width: "100%", display: "flex", alignItems: "center", gap: 12,
              background: deadlineAck ? C.teal : C.ivory,
              border: `1.5px solid ${deadlineAck ? C.teal : C.tealPale}`,
              borderRadius: 12, padding: "13px 15px",
              cursor: "pointer", outline: "none", transition: "all 0.18s",
            }}
          >
            <div style={{
              width: 18, height: 18, borderRadius: 5, flexShrink: 0,
              border: `1.5px solid ${deadlineAck ? C.tealLight : C.tealPale}`,
              background: deadlineAck ? C.tealLight : "transparent",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              {deadlineAck && <span style={{ fontSize: 10, color: C.tealDark, fontWeight: 700 }}>✓</span>}
            </div>
            <span style={{ fontSize: 14, fontFamily: "'DM Sans', sans-serif", color: deadlineAck ? "#fff" : C.charcoal, fontWeight: deadlineAck ? 600 : 400, lineHeight: 1.4 }}>
              {isSolo ? "I commit to honoring this deadline — no extensions without a real reason." : "We both agree to honor this deadline — no extensions without a real reason."}
            </span>
          </button>
        )}
      </div>

      {/* Done state */}
      {fullyDone && (
        <div style={{ background: C.teal, borderRadius: 16, padding: "20px", textAlign: "center" }}>
          <p style={{ fontSize: 18, fontFamily: "'Playfair Display', serif", fontWeight: 700, color: "#fff", margin: 0, marginBottom: 8, lineHeight: 1.4 }}>
            You're ready.
          </p>
          <p style={{ fontSize: 13, color: C.tealBg, fontFamily: "'DM Sans', sans-serif", margin: 0, lineHeight: 1.6 }}>
            {isSolo ? "You've done the work. You know what you're deciding. You have a deadline. That's enough — move forward." : "You've done the work. You know what you're deciding. You have a deadline you both agreed to. That's enough — move forward."}
          </p>
        </div>
      )}

      <p style={{ fontSize: 13, color: C.charcoalLight, fontFamily: "'DM Sans', sans-serif", fontStyle: "italic", lineHeight: 1.6, textAlign: "center" }}>
        "{step.note}"
      </p>
    </div>
  );
}

function Commit({ step, answers, set, isSolo }) {
  const pledgeList = isSolo ? (step.pledges_solo || step.pledges) : step.pledges;
  const savedPledges = answers.co_pledges || {};
  const decision = answers.co_decision || "";
  const allPledged = pledgeList.every(p => savedPledges[p.id]);
  const placeholder = isSolo ? "Write it clearly — 'I have decided to…'" : "Write it clearly — 'We have decided to…'";
  const label = isSolo ? "Make the commitment" : "Make the commitment together";
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <p style={S.body}>{isSolo ? step.description_solo || step.description : step.description}</p>
      <div>
        <p style={{ ...S.label, marginBottom: 6 }}>What did you decide?</p>
        <ExpandableTextarea
          value={decision}
          onChange={v => set({ co_decision: v })}
          placeholder={placeholder}
          minHeight={70}
          label="Your decision"
        />
      </div>
      <div>
        <p style={{ ...S.label, marginBottom: 8 }}>{label}</p>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {pledgeList.map(p => (
            <CheckRow key={p.id} checked={!!savedPledges[p.id]} text={p.text}
              onToggle={() => set({ co_pledges: { ...savedPledges, [p.id]: !savedPledges[p.id] } })} />
          ))}
        </div>
      </div>
      {allPledged && decision && (
        <div style={{ background: "#111", borderRadius: 18, padding: "24px 20px", textAlign: "center" }}>
          <div style={{ fontSize: 28, marginBottom: 10 }}>🤝</div>
          <p style={{ fontSize: 19, fontFamily: "'Playfair Display', serif", fontWeight: 700, color: "#fff", margin: 0, marginBottom: 8, lineHeight: 1.4 }}>
            {isSolo ? "You decided." : "You decided together."}
          </p>
          <p style={{ fontSize: 13, color: "#bbb", fontFamily: "'DM Sans', sans-serif", margin: 0, lineHeight: 1.6 }}>
            {isSolo ? "Now build on it. No second-guessing. Just forward." : "Now build it together. No second-guessing. No re-litigation. Just forward."}
          </p>
        </div>
      )}
    </div>
  );
}

// ─── COUPLES SUMMARY ──────────────────────────────────────────────────────────

function CouplesSummary({ code, myRole, myName, myAnswers }) {
  const [partnerData, setPartnerData] = useState(null);
  const [partnerName, setPartnerName] = useState(null);
  const [loading, setLoading] = useState(false);
  const [checked, setChecked] = useState(false);

  async function checkPartner() {
    setLoading(true);
    const data = await dbLoadPartner(code, myRole);
    setPartnerData(data ? data.answers : null);
    const partnerRole = myRole === "person_a" ? "person_b" : "person_a";
    const partnerSession = await dbLoadSession(code, partnerRole);
    setPartnerName(partnerSession?.answers?.my_name || null);
    setChecked(true);
    setLoading(false);
  }

  const myLabel = myName || (myRole === "person_a" ? "Person A" : "Person B");
  const theirLabel = partnerName || (myRole === "person_a" ? "Person B" : "Person A");

  const getFearLabel = id => FEAR_OPTIONS.find(f => f.id === id)?.label || id;
  const getMyFears = () => Object.keys(myAnswers.fear_sel || {}).filter(k => myAnswers.fear_sel[k]);
  const getTheirFears = () => partnerData ? Object.keys(partnerData.fear_sel || {}).filter(k => partnerData.fear_sel[k]) : [];
  const sharedFears = getMyFears().filter(f => getTheirFears().includes(f));
  const uniqueToMe = getMyFears().filter(f => !getTheirFears().includes(f));
  const uniqueToThem = getTheirFears().filter(f => !getMyFears().includes(f));

  // Alignment score
  function calcAlignment(pd) {
    let aligned = 0; let total = 0;
    const pairs = [
      [myAnswers.sp_gate, pd.sp_gate],
      [myAnswers.vi_gate, pd.vi_gate],
      [myAnswers.tr_gate, pd.tr_gate],
    ];
    pairs.forEach(([a, b]) => { if (a && b) { total++; if (a === b) aligned++; } });
    // Trust prompts
    TRUST_PROMPTS_COUPLES.forEach(p => {
      const a = (myAnswers.tr_responses || {})[p.id];
      const b = (pd.tr_responses || {})[p.id];
      if (a && b) { total++; if (a === b) aligned++; }
    });
    return total > 0 ? Math.round((aligned / total) * 100) : 0;
  }

  function AlignRow({ label, mine, theirs }) {
    const agree = mine === theirs;
    return (
      <div style={{ borderBottom: "1px solid #f0f0f0", padding: "10px 0" }}>
        <p style={{ ...S.label, marginBottom: 6 }}>{label}</p>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          {[[myLabel, mine], [theirLabel, theirs]].map(([name, val]) => (
            <div key={name} style={{ background: "#fafafa", borderRadius: 10, padding: "9px 12px" }}>
              <p style={{ fontSize: 10, color: "#bbb", fontFamily: "'DM Sans', sans-serif", marginBottom: 3 }}>{name}</p>
              <p style={{ fontSize: 13, color: "#333", fontFamily: "'DM Sans', sans-serif", margin: 0, fontWeight: 500 }}>{val || "—"}</p>
            </div>
          ))}
        </div>
        {agree && mine && (
          <div style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 6 }}>
            <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#4caf50", display: "inline-block" }} />
            <span style={{ fontSize: 11, color: "#4caf50", fontFamily: "'DM Sans', sans-serif" }}>Aligned ✓</span>
          </div>
        )}
        {!agree && mine && theirs && (
          <div style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 6 }}>
            <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#f0a500", display: "inline-block" }} />
            <span style={{ fontSize: 11, color: "#f0a500", fontFamily: "'DM Sans', sans-serif" }}>Worth a conversation — no wrong answer</span>
          </div>
        )}
      </div>
    );
  }

  const alignScore = partnerData ? calcAlignment(partnerData) : null;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

      {/* Header */}
      <div style={{ background: "#111", borderRadius: 18, padding: "20px", textAlign: "center" }}>
        <p style={{ fontSize: 11, letterSpacing: "0.12em", textTransform: "uppercase", color: "#888", fontFamily: "'DM Sans', sans-serif", marginBottom: 6 }}>Couples Summary</p>
        <p style={{ fontFamily: "'Playfair Display', serif", fontSize: 20, fontWeight: 700, color: "#fff", margin: 0, marginBottom: partnerName ? 6 : 0 }}>Session {code}</p>
        {myName && <p style={{ fontSize: 13, color: "#aaa", fontFamily: "'DM Sans', sans-serif", margin: 0 }}>{myLabel}{partnerName ? ` & ${theirLabel}` : ""}</p>}
      </div>

      {!checked ? (
        <div style={{ textAlign: "center", padding: "10px 0" }}>
          <p style={{ fontSize: 14, color: "#888", fontFamily: "'DM Sans', sans-serif", lineHeight: 1.6, marginBottom: 16 }}>
            Once your partner finishes using code <strong style={{ color: "#111" }}>{code}</strong>, tap below to compare.
          </p>
          <button onClick={checkPartner} disabled={loading} style={{ background: "#111", color: "#fff", border: "none", borderRadius: 50, padding: "13px 28px", fontSize: 14, fontWeight: 600, fontFamily: "'DM Sans', sans-serif", cursor: "pointer" }}>
            {loading ? "Checking…" : "Check for partner's responses →"}
          </button>
        </div>
      ) : !partnerData ? (
        <div style={{ textAlign: "center", padding: "10px 0" }}>
          <p style={{ fontSize: 14, color: "#aaa", fontFamily: "'DM Sans', sans-serif", lineHeight: 1.6 }}>
            Your partner hasn't finished yet. Share code <strong style={{ color: "#111" }}>{code}</strong> and check back when they're done.
          </p>
          <button onClick={checkPartner} style={{ marginTop: 12, background: "none", border: "1.5px solid #e0e0e0", borderRadius: 50, padding: "10px 20px", fontSize: 13, fontFamily: "'DM Sans', sans-serif", color: "#666", cursor: "pointer" }}>
            Check again
          </button>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>

          {/* ── ALIGNMENT SUMMARY (top, encouraging) ── */}
          <div style={{ background: "#f0faf2", border: "1.5px solid #a5d6a7", borderRadius: 16, padding: "18px 18px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
              <div style={{ width: 52, height: 52, borderRadius: "50%", background: "#111", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <span style={{ fontSize: 20, fontWeight: 700, color: "#fff", fontFamily: "'DM Sans', sans-serif" }}>{alignScore}%</span>
              </div>
              <div>
                <p style={{ fontSize: 15, fontWeight: 700, color: "#111", fontFamily: "'DM Sans', sans-serif", margin: 0, marginBottom: 3 }}>
                  {alignScore >= 80 ? "You're more aligned than you may feel." : alignScore >= 50 ? "You have more common ground than you think." : "You're in the same conversation — that matters."}
                </p>
                <p style={{ fontSize: 13, color: "#555", fontFamily: "'DM Sans', sans-serif", margin: 0, lineHeight: 1.5 }}>
                  {alignScore >= 80
                    ? "The hard work you've both done individually shows here. Trust what you've built."
                    : alignScore >= 50
                    ? "Where you differ isn't a problem — it's information. Use the gaps below as your starting point."
                    : "Differences don't mean you're stuck. They mean you have more to talk through — and now you know exactly where."}
                </p>
              </div>
            </div>

            {/* Shared gate alignment pills */}
            {sharedFears.length > 0 && (
              <div style={{ marginTop: 8 }}>
                <p style={{ fontSize: 11, color: "#4caf50", fontFamily: "'DM Sans', sans-serif", fontWeight: 600, marginBottom: 6 }}>● Fears you both named</p>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                  {sharedFears.map(f => <span key={f} style={{ background: "#fff", color: "#2e7d32", border: "1.5px solid #81c784", borderRadius: 50, padding: "4px 10px", fontSize: 12, fontFamily: "'DM Sans', sans-serif" }}>{getFearLabel(f)}</span>)}
                </div>
              </div>
            )}
          </div>

          {/* ── TRUST PROMPT COMPARISON ── */}
          {(() => {
            const myNotes = myAnswers.tr_notes || {};
            const theirNotes = partnerData.tr_notes || {};
            const notePrompts = TRUST_PROMPTS_COUPLES.filter(p => p.followup);
            const hasNotes = notePrompts.some(p => myNotes[p.followup.id] || theirNotes[p.followup.id]);
            if (!hasNotes) return null;
            return (
              <div>
                <p style={{ ...S.label, marginBottom: 10 }}>What you each wrote</p>
                {notePrompts.map(p => {
                  const myNote = myNotes[p.followup.id];
                  const theirNote = theirNotes[p.followup.id];
                  if (!myNote && !theirNote) return null;
                  return (
                    <div key={p.id} style={{ background: "#fafafa", borderRadius: 12, padding: "12px 14px", marginBottom: 8, border: "1.5px solid #ebebeb" }}>
                      <p style={{ fontSize: 12, fontWeight: 600, color: "#444", fontFamily: "'DM Sans', sans-serif", marginBottom: 8, lineHeight: 1.4 }}>{p.question}</p>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                        {[[myLabel, myNote], [theirLabel, theirNote]].map(([name, note]) => note ? (
                          <div key={name} style={{ background: "#fff", borderRadius: 10, padding: "10px 12px", border: "1.5px solid #e0e0e0" }}>
                            <p style={{ fontSize: 10, color: "#bbb", fontFamily: "'DM Sans', sans-serif", marginBottom: 5 }}>{name}</p>
                            <p style={{ fontSize: 13, color: "#333", fontFamily: "'DM Sans', sans-serif", margin: 0, lineHeight: 1.5, fontStyle: "italic" }}>"{note}"</p>
                          </div>
                        ) : (
                          <div key={name} style={{ background: "#f7f7f7", borderRadius: 10, padding: "10px 12px" }}>
                            <p style={{ fontSize: 10, color: "#bbb", fontFamily: "'DM Sans', sans-serif", marginBottom: 5 }}>{name}</p>
                            <p style={{ fontSize: 12, color: "#ccc", fontFamily: "'DM Sans', sans-serif", margin: 0, fontStyle: "italic" }}>Nothing written</p>
                          </div>
                        ))}
                      </div>
                      {myNote && theirNote && (
                        <div style={{ marginTop: 8, background: "#f0f4ff", border: "1.5px solid #c5cff5", borderRadius: 8, padding: "8px 12px" }}>
                          <p style={{ fontSize: 12, color: "#3949ab", fontFamily: "'DM Sans', sans-serif", margin: 0, lineHeight: 1.5 }}>
                            💡 You both answered this one. Read each other's responses before your next conversation — don't interpret, ask.
                          </p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })()}

          {/* ── GATE RESPONSES ── */}
          <div>
            <p style={{ ...S.label, marginBottom: 4 }}>Gate responses</p>
            <AlignRow label="Spiritual Alignment"
              mine={myAnswers.sp_gate === "yes" ? "Yes — aligned" : myAnswers.sp_gate === "no" ? "Not yet" : "—"}
              theirs={partnerData.sp_gate === "yes" ? "Yes — aligned" : partnerData.sp_gate === "no" ? "Not yet" : "—"} />
            <AlignRow label="Family Vision Alignment"
              mine={myAnswers.vi_gate === "yes" ? "Yes — aligns" : myAnswers.vi_gate === "no" ? "Not yet" : "—"}
              theirs={partnerData.vi_gate === "yes" ? "Yes — aligns" : partnerData.vi_gate === "no" ? "Not yet" : "—"} />
          </div>

          {/* ── FEARS BREAKDOWN ── */}
          {(uniqueToMe.length > 0 || uniqueToThem.length > 0) && (
            <div>
              <p style={{ fontSize: 11, color: "#f0a500", fontFamily: "'DM Sans', sans-serif", marginBottom: 8, fontWeight: 600 }}>● Fears worth exploring together</p>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                {[[myLabel, uniqueToMe], [theirLabel, uniqueToThem]].map(([name, list]) => (
                  <div key={name}>
                    <p style={{ fontSize: 10, color: "#bbb", fontFamily: "'DM Sans', sans-serif", marginBottom: 4 }}>{name} only</p>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                      {list.map(f => <span key={f} style={{ background: "#f5f5f5", color: "#555", borderRadius: 50, padding: "4px 10px", fontSize: 11, fontFamily: "'DM Sans', sans-serif" }}>{getFearLabel(f)}</span>)}
                      {list.length === 0 && <span style={{ fontSize: 12, color: "#ccc", fontFamily: "'DM Sans', sans-serif" }}>None unique</span>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── WRITTEN THOUGHTS ── */}
          {(myAnswers.fear_extra || partnerData.fear_extra) && (
            <div>
              <p style={{ ...S.label, marginBottom: 8 }}>Additional thoughts</p>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                {[[myLabel, myAnswers.fear_extra], [theirLabel, partnerData.fear_extra]].map(([name, text]) => text ? (
                  <div key={name} style={{ background: "#fafafa", borderRadius: 12, padding: "12px" }}>
                    <p style={{ fontSize: 10, color: "#bbb", fontFamily: "'DM Sans', sans-serif", marginBottom: 5 }}>{name}</p>
                    <p style={{ fontSize: 13, color: "#333", fontFamily: "'DM Sans', sans-serif", margin: 0, lineHeight: 1.5, fontStyle: "italic" }}>"{text}"</p>
                  </div>
                ) : null)}
              </div>
            </div>
          )}

          {/* ── CLOSING ENCOURAGEMENT ── */}
          <div style={{ background: "#f7f7f7", borderRadius: 14, padding: "14px 16px" }}>
            <p style={{ fontSize: 13, color: "#888", fontFamily: "'DM Sans', sans-serif", margin: 0, lineHeight: 1.7 }}>
              Use this as your starting point — not a scorecard. Where you see differences, get <strong style={{ color: "#111" }}>curious before you get defensive.</strong> The goal isn't to agree on everything — it's to understand each other well enough to decide together.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── EXPORT ───────────────────────────────────────────────────────────────────

function exportSummary(answers, sessionCode, role, myName) {
  const date = new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  const nameLabel = myName || (role === "person_a" ? "Person A" : role === "person_b" ? "Person B" : null);

  const validFears = Object.keys(answers.fear_sel || {}).filter(k => answers.fear_sel[k] && (answers.fear_categorized || {})[k] === "valid");
  const fearBased = Object.keys(answers.fear_sel || {}).filter(k => answers.fear_sel[k] && (answers.fear_categorized || {})[k] === "fear_based");
  const uncatFears = Object.keys(answers.fear_sel || {}).filter(k => answers.fear_sel[k] && !(answers.fear_categorized || {})[k]);
  const getFearLabel = id => FEAR_OPTIONS.find(f => f.id === id)?.label || id;
  const getFearEmoji = id => FEAR_OPTIONS.find(f => f.id === id)?.emoji || "";

  function tagPills(tags, color) {
    if (!tags || tags.length === 0) return `<span style="color:#bbb;font-style:italic;">None added</span>`;
    return tags.map(t => `<span style="display:inline-block;margin:2px 4px 2px 0;padding:4px 12px;border-radius:50px;font-size:12px;font-weight:600;background:${color === "pro" ? "#e8f5e9" : "#fff3e0"};color:${color === "pro" ? "#2e7d32" : "#e65100"};border:1.5px solid ${color === "pro" ? "#81c784" : "#ffb74d"};">${t}</span>`).join("");
  }

  function fearPills(ids, bg, border, color) {
    if (!ids || ids.length === 0) return `<span style="color:#bbb;font-style:italic;">None</span>`;
    return ids.map(id => `<span style="display:inline-block;margin:2px 4px 2px 0;padding:4px 12px;border-radius:50px;font-size:12px;background:${bg};color:${color};border:1.5px solid ${border};">${getFearEmoji(id)} ${getFearLabel(id)}</span>`).join("");
  }

  function sectionHeader(number, emoji, title) {
    return `
      <div style="margin-top:32px;margin-bottom:12px;padding-bottom:8px;border-bottom:2px solid #111;">
        <div style="display:flex;align-items:center;gap:10px;">
          <div style="width:28px;height:28px;border-radius:50%;background:#111;display:flex;align-items:center;justify-content:center;flex-shrink:0;">
            <span style="font-size:12px;color:#fff;font-weight:700;">${number}</span>
          </div>
          <span style="font-size:18px;">${emoji}</span>
          <h2 style="margin:0;font-size:17px;font-family:Georgia,serif;color:#111;">${title}</h2>
        </div>
      </div>`;
  }

  function row(label, value) {
    return `<div style="margin-bottom:8px;"><span style="font-size:11px;text-transform:uppercase;letter-spacing:0.08em;color:#999;font-family:sans-serif;">${label}</span><div style="font-size:14px;color:#222;margin-top:3px;line-height:1.6;font-family:sans-serif;">${value || "<span style='color:#bbb;font-style:italic;'>Not provided</span>"}</div></div>`;
  }

  function yesNo(val) {
    if (val === "yes") return `<span style="color:#2e7d32;font-weight:700;">✓ Yes</span>`;
    if (val === "no") return `<span style="color:#e65100;font-weight:600;">○ Not fully yet</span>`;
    return `<span style="color:#bbb;font-style:italic;">—</span>`;
  }

  const optionBlock = (name, prosKey, consKey) => name ? `
    <div style="margin-bottom:14px;border:1.5px solid #e0e0e0;border-radius:12px;overflow:hidden;">
      <div style="background:#111;padding:10px 16px;">
        <p style="margin:0;font-size:15px;font-weight:700;color:#fff;font-family:Georgia,serif;">${name}</p>
      </div>
      <div style="padding:12px 16px;background:#fafafa;">
        <p style="margin:0 0 6px;font-size:11px;text-transform:uppercase;letter-spacing:0.08em;color:#2e7d32;font-family:sans-serif;font-weight:700;">✅ Pros</p>
        <div style="margin-bottom:10px;">${tagPills(answers[prosKey], "pro")}</div>
        <p style="margin:0 0 6px;font-size:11px;text-transform:uppercase;letter-spacing:0.08em;color:#e65100;font-family:sans-serif;font-weight:700;">⚠️ Cons</p>
        <div>${tagPills(answers[consKey], "con")}</div>
      </div>
    </div>` : "";

  const html = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8"/>
  <title>Decide Well — Session Summary</title>
  <style>
    @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=Playfair+Display:wght@700&display=swap');
    * { box-sizing: border-box; }
    body { font-family: 'DM Sans', sans-serif; color: #111; background: #fff; margin: 0; padding: 0; }
    @media print {
      .no-print { display: none !important; }
      body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    }
  </style>
</head>
<body style="max-width:720px;margin:0 auto;padding:48px 40px 80px;">

  <!-- Header -->
  <div style="text-align:center;margin-bottom:40px;padding-bottom:32px;border-bottom:3px solid #111;">
    <p style="font-size:11px;letter-spacing:0.2em;text-transform:uppercase;color:#bbb;margin:0 0 8px;font-family:sans-serif;">Decide Well</p>
    <h1 style="font-family:'Playfair Display',Georgia,serif;font-size:30px;margin:0 0 10px;color:#111;">Session Summary</h1>
    <p style="font-size:14px;color:#888;margin:0;font-family:sans-serif;">${date}${sessionCode ? ` &nbsp;·&nbsp; Session <strong>${sessionCode}</strong>` : ""}${nameLabel ? ` &nbsp;·&nbsp; <strong>${nameLabel}</strong>'s responses` : ""}</p>
  </div>

  <!-- 01 Spiritual Alignment -->
  ${sectionHeader("01", "🙏", "Spiritual Alignment")}
  <div style="background:#fafafa;border-radius:12px;padding:14px 16px;">
    ${row("Clarity & Unity", answers.sp_gate === "yes" ? "✅ Yes — aligned and ready to move forward" : answers.sp_gate === "no" ? "⏸ Not yet — more time needed" : "—")}
    <div style="margin-top:8px;">
      <p style="font-size:11px;text-transform:uppercase;letter-spacing:0.08em;color:#999;margin:0 0 6px;font-family:sans-serif;">Checklist</p>
      ${(STEPS.find(s => s.id === "spiritual")?.questions || []).map(q => `<div style="display:flex;align-items:flex-start;gap:8px;margin-bottom:5px;font-size:13px;color:#444;font-family:sans-serif;line-height:1.5;">${(answers.sp_checks || {})[q.id] ? "✅" : "☐"} ${q.text}</div>`).join("")}
    </div>
    ${answers.sp_notes ? `<div style="margin-top:10px;padding:12px;background:#fff;border:1.5px solid #e0e0e0;border-radius:10px;"><p style="font-size:11px;text-transform:uppercase;letter-spacing:0.08em;color:#999;margin:0 0 5px;font-family:sans-serif;">📝 My notes</p><p style="font-size:13px;color:#444;font-family:sans-serif;line-height:1.6;margin:0;font-style:italic;">"${answers.sp_notes}"</p></div>` : ""}
    ${answers.sp_discuss ? `<div style="margin-top:8px;padding:12px;background:#fff8f1;border:1.5px solid #ffcc80;border-radius:10px;"><p style="font-size:11px;text-transform:uppercase;letter-spacing:0.08em;color:#e65100;margin:0 0 5px;font-family:sans-serif;">📋 Thoughts to discuss later</p><p style="font-size:13px;color:#444;font-family:sans-serif;line-height:1.6;margin:0;font-style:italic;">"${answers.sp_discuss}"</p></div>` : ""}
  </div>

  <!-- 02 Family Vision -->
  ${sectionHeader("02", "🎯", "Family Vision Alignment")}
  <div style="background:#fafafa;border-radius:12px;padding:14px 16px;">
    ${row("Supports our vision", answers.vi_gate === "yes" ? "✅ Yes — this decision points in the right direction" : answers.vi_gate === "no" ? "✕ No — doesn't align" : "—")}
    <div style="margin-top:8px;">
      <p style="font-size:11px;text-transform:uppercase;letter-spacing:0.08em;color:#999;margin:0 0 6px;font-family:sans-serif;">Vision Anchors</p>
      ${(answers.vi_anchors || STEPS[1].vision_anchors).map(a => `<div style="display:flex;align-items:center;gap:8px;margin-bottom:5px;font-size:13px;color:#444;font-family:sans-serif;">${a.emoji} ${a.text}</div>`).join("")}
    </div>
  </div>

  <!-- 03 Reality Mapping -->
  ${sectionHeader("03", "⚖️", "Reality Mapping")}
  ${optionBlock(answers.opt_a, "tags_pros_a", "tags_cons_a")}
  ${optionBlock(answers.opt_b, "tags_pros_b", "tags_cons_b")}
  ${answers.opt_c ? optionBlock(answers.opt_c, "tags_pros_c", "tags_cons_c") : ""}

  <!-- 04 Emotional Processing -->
  ${sectionHeader("04", "❤️", "Emotional Processing")}
  <div style="background:#fafafa;border-radius:12px;padding:14px 16px;">
    <div style="margin-bottom:12px;">
      <p style="font-size:11px;text-transform:uppercase;letter-spacing:0.08em;color:#2e7d32;font-weight:700;margin:0 0 6px;font-family:sans-serif;">✅ Valid Concerns — need a plan</p>
      <div>${fearPills([...validFears, ...(answers.fear_custom || []).filter(f => (answers.fear_categorized || {})[f.id] === "valid").map(f => f.id)], "#e8f5e9", "#81c784", "#2e7d32")}</div>
    </div>
    <div style="margin-bottom:12px;">
      <p style="font-size:11px;text-transform:uppercase;letter-spacing:0.08em;color:#e65100;font-weight:700;margin:0 0 6px;font-family:sans-serif;">🙏 Fear-Based — need surrender</p>
      <div>${fearPills([...fearBased, ...(answers.fear_custom || []).filter(f => (answers.fear_categorized || {})[f.id] === "fear_based").map(f => f.id)], "#fff8f1", "#ffcc80", "#e65100")}</div>
    </div>
    ${uncatFears.length > 0 || (answers.fear_custom || []).some(f => !(answers.fear_categorized || {})[f.id]) ? `<div style="margin-bottom:12px;"><p style="font-size:11px;text-transform:uppercase;letter-spacing:0.08em;color:#aaa;margin:0 0 6px;font-family:sans-serif;">Unsorted</p><div>${fearPills([...uncatFears, ...(answers.fear_custom || []).filter(f => !(answers.fear_categorized || {})[f.id]).map(f => f.id)], "#f5f5f5", "#e0e0e0", "#666")}</div></div>` : ""}
    ${(answers.fear_custom || []).length > 0 ? `<div style="margin-bottom:8px;"><p style="font-size:11px;text-transform:uppercase;letter-spacing:0.08em;color:#999;margin:0 0 6px;font-family:sans-serif;">Custom fears added</p><div style="display:flex;flex-wrap:wrap;gap:5px;">${(answers.fear_custom || []).map(f => `<span style="display:inline-block;padding:4px 10px;border-radius:50px;font-size:12px;background:#f5f5f5;color:#555;border:1.5px solid #e0e0e0;font-family:sans-serif;">✏️ ${f.label}</span>`).join("")}</div></div>` : ""}
    ${answers.fear_extra ? `<div style="margin-top:8px;padding:12px;background:#fff;border:1.5px solid #e0e0e0;border-radius:10px;font-size:13px;color:#444;font-family:sans-serif;line-height:1.6;font-style:italic;">"${answers.fear_extra}"</div>` : ""}
  </div>

  <!-- 05 Realignment & Recommitment -->
  ${sectionHeader("05", "🤝", "Realignment & Recommitment")}
  <div style="display:flex;flex-direction:column;gap:10px;">
    ${TRUST_PROMPTS.map((p, i) => {
      const r = (answers.tr_responses || {})[p.id];
      const n = p.followup ? (answers.tr_notes || {})[p.followup.id] : null;
      return `<div style="border:1.5px solid ${r === "yes" ? "#a5d6a7" : r === "no" ? "#ffcc80" : "#e0e0e0"};border-radius:12px;padding:12px 14px;background:#fafafa;">
        <p style="font-size:13px;font-weight:600;color:#222;margin:0 0 6px;font-family:sans-serif;line-height:1.5;">${i + 1}. ${p.question}</p>
        <p style="margin:0;font-size:13px;font-family:sans-serif;">${yesNo(r)}</p>
        ${n ? `<p style="margin:8px 0 0;font-size:13px;color:#555;font-style:italic;font-family:sans-serif;line-height:1.5;">"${n}"</p>` : ""}
      </div>`;
    }).join("")}
  </div>

  <!-- 06 Decision Threshold -->
  ${sectionHeader("06", "🎯", "Decision Threshold")}
  <div style="background:#fafafa;border-radius:12px;padding:14px 16px;">
    <div style="margin-bottom:14px;padding:16px;background:#111;border-radius:10px;">
      <p style="font-size:11px;text-transform:uppercase;letter-spacing:0.08em;color:#888;margin:0 0 6px;font-family:sans-serif;">What we're deciding</p>
      <p style="font-size:16px;font-family:'Playfair Display',Georgia,serif;font-weight:700;color:#fff;margin:0;line-height:1.5;">"${answers.th_decision_text || "Not written yet"}"</p>
    </div>
    ${row("Deadline", answers.th_deadline || "—")}
    ${row("Both agreed to deadline", answers.th_deadline_ack ? "✅ Yes" : "Not yet")}
    <div style="margin-top:8px;">
      <p style="font-size:11px;text-transform:uppercase;letter-spacing:0.08em;color:#999;margin:0 0 6px;font-family:sans-serif;">Readiness Check</p>
      ${READINESS_PROMPTS.map(p => `<div style="display:flex;align-items:flex-start;gap:8px;margin-bottom:5px;font-size:13px;color:#444;font-family:sans-serif;line-height:1.5;">${(answers.th_readiness || {})[p.id] ? "✅" : "☐"} ${p.text}</div>`).join("")}
    </div>
    ${answers.th_unresolved ? `<div style="margin-top:10px;padding:12px;background:#fff3e0;border:1.5px solid #ffcc80;border-radius:10px;font-size:13px;color:#e65100;font-family:sans-serif;line-height:1.6;"><strong>Still working through:</strong> ${answers.th_unresolved}</div>` : ""}
  </div>

  <!-- 07 Unified Commitment -->
  ${sectionHeader("07", "✝️", "Unified Commitment")}
  <div style="background:#111;border-radius:14px;padding:20px 22px;margin-bottom:12px;">
    <p style="font-size:11px;text-transform:uppercase;letter-spacing:0.1em;color:#888;margin:0 0 8px;font-family:sans-serif;">Our Decision</p>
    <p style="font-size:18px;font-family:'Playfair Display',Georgia,serif;font-weight:700;color:#fff;margin:0;line-height:1.5;">"${answers.co_decision || "Not written yet"}"</p>
  </div>
  <div style="border:1.5px solid #e0e0e0;border-radius:12px;padding:14px 16px;">
    <p style="font-size:11px;text-transform:uppercase;letter-spacing:0.08em;color:#999;margin:0 0 10px;font-family:sans-serif;">Our Commitments</p>
    ${(STEPS[6].pledges || []).map(p => `<div style="display:flex;align-items:flex-start;gap:8px;margin-bottom:8px;font-size:14px;color:#333;font-family:sans-serif;line-height:1.5;">${(answers.co_pledges || {})[p.id] ? "✅" : "☐"} ${p.text}</div>`).join("")}
  </div>

  <!-- Footer -->
  <div style="margin-top:48px;padding-top:24px;border-top:1px solid #e0e0e0;text-align:center;">
    <p style="font-size:14px;color:#888;font-style:italic;line-height:1.8;margin:0 0 4px;font-family:sans-serif;">There is no option without loss. There is no option without discomfort.</p>
    <p style="font-size:14px;color:#888;font-style:italic;line-height:1.8;margin:0 0 20px;font-family:sans-serif;">But there is a path that better aligns with what you believe you're called to build.</p>
    <p style="font-size:11px;letter-spacing:0.15em;text-transform:uppercase;color:#ccc;margin:0;font-family:sans-serif;">Decide Well</p>
  </div>

  <!-- Print button -->
  <div class="no-print" style="position:fixed;bottom:24px;right:24px;">
    <button onclick="window.print()" style="background:#111;color:#fff;border:none;border-radius:50px;padding:12px 24px;font-size:14px;font-weight:600;cursor:pointer;font-family:sans-serif;box-shadow:0 4px 16px rgba(0,0,0,0.2);">
      🖨️ Save as PDF
    </button>
  </div>

</body>
</html>`;

  const win = window.open("", "_blank");
  if (win) {
    win.document.write(html);
    win.document.close();
  }
}

// ─── FLOW MODAL ───────────────────────────────────────────────────────────────

function FlowModal({ onClose }) {
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(8,80,65,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100, padding: 20 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: "#fff", borderRadius: 22, padding: "24px 20px", maxWidth: 340, width: "100%", maxHeight: "80vh", overflowY: "auto", border: `1.5px solid ${C.tealPale}` }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18 }}>
          <p style={{ fontFamily: "'Playfair Display', serif", fontSize: 18, fontWeight: 700, color: C.tealDark, margin: 0 }}>Decision Flow</p>
          <button onClick={onClose} style={{ background: C.tealBg, border: "none", borderRadius: 8, padding: "4px 10px", cursor: "pointer", fontSize: 16, color: C.teal, lineHeight: 1 }}>×</button>
        </div>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "stretch" }}>
          {FLOW_NODES.map((node, i) => (
            <div key={i} style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
              <div style={{ background: node.final ? C.teal : C.tealBg, border: `1.5px solid ${node.final ? C.teal : C.tealPale}`, borderRadius: 12, padding: "11px 16px", width: "100%", textAlign: "center" }}>
                <span style={{ fontSize: 13, fontWeight: 700, fontFamily: "'DM Sans', sans-serif", color: node.final ? "#fff" : C.tealDark }}>{node.label}</span>
                {!node.final && (
                  <div style={{ display: "flex", justifyContent: "space-between", marginTop: 5 }}>
                    <span style={{ fontSize: 11, color: C.tealMid, fontFamily: "'DM Sans', sans-serif" }}>✓ {node.pass}</span>
                    <span style={{ fontSize: 11, color: C.gold, fontFamily: "'DM Sans', sans-serif" }}>✕ {node.fail}</span>
                  </div>
                )}
              </div>
              {i < FLOW_NODES.length - 1 && <div style={{ width: 1, height: 14, background: C.tealPale }} />}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── WELCOME ──────────────────────────────────────────────────────────────────

function Welcome({ onSolo, onCouplesCreate, onCouplesJoin, savedData, onResume }) {
  const [joining, setJoining] = useState(false);
  const [joinCode, setJoinCode] = useState("");
  const [creating, setCreating] = useState(false);
  const [createName, setCreateName] = useState("");
  const [joinName, setJoinName] = useState("");

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      {savedData && (
        <div style={{ background: C.tealBg, borderRadius: 16, padding: "16px 18px", border: `1.5px solid ${C.tealPale}` }}>
          <p style={{ ...S.label, marginBottom: 6, color: C.teal }}>Welcome back{savedData.myName ? `, ${savedData.myName}` : ""}</p>
          <p style={{ fontSize: 14, color: C.tealDark, fontFamily: "'DM Sans', sans-serif", marginBottom: 12, lineHeight: 1.5 }}>
            You have a session in progress — Step {savedData.idx + 1} of {STEPS.length}.
          </p>
          <button onClick={onResume} style={{ background: C.teal, color: "#fff", border: "none", borderRadius: 50, padding: "10px 22px", fontSize: 13, fontWeight: 600, fontFamily: "'DM Sans', sans-serif", cursor: "pointer" }}>
            Resume where I left off →
          </button>
        </div>
      )}

      {/* Solo */}
      <div style={{ borderRadius: 16, border: `1.5px solid ${C.tealPale}`, padding: "18px 18px 16px", background: C.ivory }}>
        <p style={{ fontSize: 15, fontWeight: 700, color: C.tealDark, fontFamily: "'DM Sans', sans-serif", marginBottom: 4 }}>Solo Mode</p>
        <p style={{ fontSize: 13, color: C.charcoalMid, fontFamily: "'DM Sans', sans-serif", lineHeight: 1.5, marginBottom: 12 }}>Work through the framework on your own. Progress auto-saves as you go.</p>
        <button onClick={onSolo} style={{ background: C.teal, color: "#fff", border: "none", borderRadius: 50, padding: "10px 22px", fontSize: 13, fontWeight: 600, fontFamily: "'DM Sans', sans-serif", cursor: "pointer" }}>Start Solo →</button>
      </div>

      {/* Couples */}
      <div style={{ borderRadius: 16, border: `1.5px solid ${C.tealPale}`, padding: "18px 18px 16px", background: C.ivory }}>
        <p style={{ fontSize: 15, fontWeight: 700, color: C.tealDark, fontFamily: "'DM Sans', sans-serif", marginBottom: 4 }}>👫 Couples Mode</p>
        <p style={{ fontSize: 13, color: C.charcoalMid, fontFamily: "'DM Sans', sans-serif", lineHeight: 1.5, marginBottom: 12 }}>
          Each person answers separately on their own device, then see a side-by-side view of where you align.
        </p>

        {!creating && !joining && (
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button onClick={() => setCreating(true)} style={{ background: C.teal, color: "#fff", border: "none", borderRadius: 50, padding: "10px 20px", fontSize: 13, fontWeight: 600, fontFamily: "'DM Sans', sans-serif", cursor: "pointer" }}>
              Create a session
            </button>
            <button onClick={() => setJoining(true)} style={{ background: "none", border: `1.5px solid ${C.tealPale}`, borderRadius: 50, padding: "10px 20px", fontSize: 13, fontFamily: "'DM Sans', sans-serif", color: C.teal, cursor: "pointer" }}>
              Join with a code
            </button>
          </div>
        )}

        {/* Create flow — name entry */}
        {creating && (
          <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 4 }}>
            <div>
              <p style={{ ...S.label, marginBottom: 6 }}>What's your name?</p>
              <input
                value={createName}
                onChange={e => setCreateName(e.target.value)}
                placeholder="e.g. David"
                autoFocus
                style={{ ...S.input, width: "100%", boxSizing: "border-box", fontSize: 15 }}
              />
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button
                onClick={() => createName.trim() && onCouplesCreate(createName.trim())}
                style={{ flex: 1, background: createName.trim() ? "#111" : "#eee", color: createName.trim() ? "#fff" : "#bbb", border: "none", borderRadius: 50, padding: "11px", fontSize: 13, fontWeight: 600, fontFamily: "'DM Sans', sans-serif", cursor: createName.trim() ? "pointer" : "default" }}>
                Create session →
              </button>
              <button onClick={() => setCreating(false)} style={{ background: "none", border: "1.5px solid #e0e0e0", borderRadius: 50, padding: "11px 16px", fontSize: 13, fontFamily: "'DM Sans', sans-serif", color: "#888", cursor: "pointer" }}>
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Join flow — code + name entry */}
        {joining && (
          <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 4 }}>
            <div>
              <p style={{ ...S.label, marginBottom: 6 }}>What's your name?</p>
              <input
                value={joinName}
                onChange={e => setJoinName(e.target.value)}
                placeholder="e.g. Sarah"
                style={{ ...S.input, width: "100%", boxSizing: "border-box", fontSize: 15 }}
              />
            </div>
            <div>
              <p style={{ ...S.label, marginBottom: 6 }}>Session code from your partner</p>
              <input
                value={joinCode}
                onChange={e => setJoinCode(e.target.value.toUpperCase())}
                maxLength={4}
                placeholder="e.g. WXKP"
                style={{ ...S.input, width: "100%", boxSizing: "border-box", letterSpacing: "0.2em", fontSize: 18, textAlign: "center" }}
              />
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button
                onClick={() => joinCode.length === 4 && joinName.trim() && onCouplesJoin(joinCode, "person_b", joinName.trim())}
                style={{ flex: 1, background: joinCode.length === 4 && joinName.trim() ? "#111" : "#eee", color: joinCode.length === 4 && joinName.trim() ? "#fff" : "#bbb", border: "none", borderRadius: 50, padding: "11px", fontSize: 13, fontWeight: 600, fontFamily: "'DM Sans', sans-serif", cursor: joinCode.length === 4 && joinName.trim() ? "pointer" : "default" }}>
                Join session →
              </button>
              <button onClick={() => setJoining(false)} style={{ background: "none", border: "1.5px solid #e0e0e0", borderRadius: 50, padding: "11px 16px", fontSize: 13, fontFamily: "'DM Sans', sans-serif", color: "#888", cursor: "pointer" }}>
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── MAIN APP ─────────────────────────────────────────────────────────────────

export default function App() {
  const [screen, setScreen] = useState("welcome");
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [visible, setVisible] = useState(true);
  const [showFlow, setShowFlow] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [sessionCode, setSessionCode] = useState(null);
  const [myRole, setMyRole] = useState(null);
  const [myName, setMyName] = useState("");
  const [savedData, setSavedData] = useState(null);

  useEffect(() => {
    const d = localLoad();
    if (d) setSavedData(d);
  }, []);

  useEffect(() => {
    if (screen !== "session") return;
    const answersWithName = { ...answers, my_name: myName };
    const payload = { idx, answers, sessionCode, myRole, myName, ts: Date.now() };
    localSave(payload);
    if (sessionCode && myRole) dbSaveSession(sessionCode, myRole, answersWithName, idx);
    setLastSaved(Date.now());
  }, [answers, idx, screen, myName, myRole, sessionCode]);

  function setA(patch) { setAnswers(a => ({ ...a, ...patch })); }

  function go(dir) {
    setVisible(false);
    setTimeout(() => { setIdx(i => i + dir); setVisible(true); }, 180);
  }

  function startSolo() {
    localClear(); setIdx(0); setAnswers({}); setSessionCode(null); setMyRole(null); setMyName("");
    setScreen("session");
  }

  function startCouplesCreate(name) {
    const code = genCode();
    localClear(); setIdx(0); setAnswers({}); setSessionCode(code); setMyRole("person_a"); setMyName(name);
    setScreen("session");
  }

  async function startCouplesJoin(code, role, name) {
    // Load person_a's session to pre-fill shared decision context
    const partnerData = await dbLoadSession(code, "person_a");
    const partnerAnswers = partnerData?.answers || {};

    // Load any existing progress for person_b
    const existing = await dbLoadSession(code, role);

    let startingAnswers = {};
    if (existing) {
      startingAnswers = existing.answers || {};
    } else {
      // Pre-fill shared decision fields from person_a
      startingAnswers = {
        opt_a: partnerAnswers.opt_a || "",
        opt_b: partnerAnswers.opt_b || "",
        opt_c: partnerAnswers.opt_c || "",
        vi_anchors: partnerAnswers.vi_anchors || null,
      };
    }

    setIdx(existing ? existing.step_idx || 0 : 0);
    setAnswers(startingAnswers);
    setSessionCode(code);
    setMyRole(role);
    setMyName(name);
    setScreen("session");
  }

  function resumeSaved() {
    if (!savedData) return;
    setIdx(savedData.idx || 0); setAnswers(savedData.answers || {});
    setSessionCode(savedData.sessionCode || null); setMyRole(savedData.myRole || null);
    setMyName(savedData.myName || "");
    setScreen("session");
  }

  function canGo() {
    const step = STEPS[idx];
    if (step.id === "decision") return (answers.opt_a || "").trim().length > 1 && (answers.opt_b || "").trim().length > 1;
    if (step.id === "spiritual") return answers.sp_gate === "yes" || answers.sp_gate === "no";
    if (step.id === "vision") return answers.vi_gate === "yes" || answers.vi_gate === "no";
    if (step.id === "reality") return (answers.opt_a || "").length > 1 && (answers.opt_b || "").length > 1;
    if (step.id === "emotional") return Object.values(answers.fear_sel || {}).some(Boolean) || (answers.fear_custom || []).length > 0;
    const prompts = !sessionCode ? TRUST_PROMPTS_SOLO : TRUST_PROMPTS_COUPLES;
    if (step.id === "trust") return prompts.every(p => (answers.tr_responses || {})[p.id]);
    if (step.id === "threshold") {
      const hasDecision = (answers.th_decision_text || "").trim().length > 3;
      const hasDeadline = (answers.th_deadline || "").trim().length > 3;
      const hasAck = !!answers.th_deadline_ack;
      const allReady = READINESS_PROMPTS.every(p => !!(answers.th_readiness || {})[p.id]);
      console.log("Threshold:", { allReady, hasDecision, hasDeadline, hasAck, readiness: answers.th_readiness });
      return hasDecision && hasDeadline && hasAck;
    }
    if (step.id === "commit") {
      const commitStep = STEPS.find(s => s.id === "commit");
      const pledges = !sessionCode ? commitStep.pledges_solo : commitStep.pledges;
      return pledges.every(p => (answers.co_pledges || {})[p.id]) && (answers.co_decision || "").length > 5;
    }
    return true;
  }

  function renderStep() {
    const step = STEPS[idx];
    const isSolo = !sessionCode;
    if (step.id === "decision") return <DecisionSetup answers={answers} set={setA} isSolo={isSolo} />;
    if (step.id === "spiritual") return <Spiritual step={step} answers={answers} set={setA} isSolo={isSolo} />;
    if (step.id === "vision") return <Vision step={step} answers={answers} set={setA} isSolo={isSolo} />;
    if (step.id === "reality") return <Reality answers={answers} set={setA} isSolo={isSolo} />;
    if (step.id === "emotional") return <Emotional answers={answers} set={setA} isSolo={isSolo} />;
    if (step.id === "trust") return <Trust step={step} answers={answers} set={setA} isSolo={isSolo} />;
    if (step.id === "threshold") return <Threshold step={step} answers={answers} set={setA} isSolo={isSolo} />;
    if (step.id === "commit") return <Commit step={step} answers={answers} set={setA} isSolo={isSolo} />;
  }

  const step = STEPS[idx];
  const isLast = idx === STEPS.length - 1;

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,wght@0,300;0,400;0,500;0,600;0,700;1,400&family=Playfair+Display:wght@600;700&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { background: ${C.ivory}; }
        textarea:focus, input:focus { border-color: ${C.teal} !important; }
        @keyframes fadeUp { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>

      {showFlow && <FlowModal onClose={() => setShowFlow(false)} />}

      <div style={{ minHeight: "100vh", background: C.ivory, display: "flex", flexDirection: "column", alignItems: "center", padding: "28px 16px 64px" }}>

        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%", maxWidth: 500, marginBottom: 22 }}>
          <div style={{ cursor: "pointer" }} onClick={() => setScreen("welcome")}>
            <p style={{ fontSize: 11, letterSpacing: "0.18em", textTransform: "uppercase", color: C.teal, fontFamily: "'DM Sans', sans-serif", marginBottom: 4, fontWeight: 600 }}>Decide Well</p>
            <div style={{ width: 28, height: 2, background: C.teal }} />
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            {screen === "session" && <SaveBadge lastSaved={lastSaved} />}
            <button onClick={() => setShowFlow(true)} style={{ background: C.tealBg, border: `1.5px solid ${C.tealPale}`, borderRadius: 50, padding: "7px 15px", fontSize: 12, fontFamily: "'DM Sans', sans-serif", color: C.teal, cursor: "pointer", outline: "none", fontWeight: 600 }}>
              ⬡ Flow
            </button>
          </div>
        </div>

        {/* Main card */}
        <div style={{ width: "100%", maxWidth: 500, background: "#fff", borderRadius: 24, padding: "26px 22px 22px", boxShadow: `0 2px 28px rgba(8,80,65,0.08)`, border: `1px solid ${C.tealBg}`, animation: "fadeUp 0.35s ease" }}>

          {screen === "welcome" && (
            <>
              <div style={{ marginBottom: 22 }}>
                <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 24, fontWeight: 700, color: C.tealDark, marginBottom: 8, lineHeight: 1.3 }}>Let's work through this.</h2>
                <p style={{ fontSize: 14, color: C.charcoalMid, fontFamily: "'DM Sans', sans-serif", lineHeight: 1.65 }}>A structured 8-step framework for making big decisions — with clarity, not chaos.</p>
              </div>

              {/* AI Coach banner */}
              <a href="https://coach.decidewell.app" target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none", display: "block", marginBottom: 18 }}>
                <div style={{ background: C.tealDark, borderRadius: 14, padding: "13px 16px", display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer" }}>
                  <div>
                    <p style={{ fontSize: 11, color: C.tealLight, fontFamily: "'DM Sans', sans-serif", fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", margin: 0, marginBottom: 3 }}>New — AI Decision Coach</p>
                    <p style={{ fontSize: 13, color: C.tealBg, fontFamily: "'DM Sans', sans-serif", margin: 0, lineHeight: 1.4 }}>Prefer a guided conversation? Try the coach.</p>
                  </div>
                  <span style={{ fontSize: 18, color: C.tealLight, flexShrink: 0, marginLeft: 10 }}>→</span>
                </div>
              </a>
              <Welcome onSolo={startSolo} onCouplesCreate={startCouplesCreate} onCouplesJoin={startCouplesJoin} savedData={savedData} onResume={resumeSaved} />
            </>
          )}

          {screen === "session" && (
            <>
              {sessionCode && (
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, background: C.tealBg, borderRadius: 10, padding: "8px 12px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{ fontSize: 11, color: C.teal, fontFamily: "'DM Sans', sans-serif" }}>👫 Couples</span>
                    <span style={{ background: C.teal, color: C.tealBg, borderRadius: 6, padding: "2px 9px", fontSize: 12, fontFamily: "'DM Sans', sans-serif", fontWeight: 700, letterSpacing: "0.1em" }}>{sessionCode}</span>
                    {myName && <span style={{ fontSize: 12, color: C.tealDark, fontFamily: "'DM Sans', sans-serif", fontWeight: 600 }}>· {myName}</span>}
                  </div>
                  <button onClick={() => navigator.clipboard?.writeText(sessionCode).catch(() => {})}
                    style={{ background: "none", border: "none", cursor: "pointer", fontSize: 11, color: C.teal, fontFamily: "'DM Sans', sans-serif" }}>
                    Copy code
                  </button>
                </div>
              )}

              <Dots current={idx} total={STEPS.length} />

              <div style={{ opacity: visible ? 1 : 0, transform: visible ? "translateY(0)" : "translateY(10px)", transition: "opacity 0.18s ease, transform 0.18s ease" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 5 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: C.tealLight, letterSpacing: "0.12em", fontFamily: "'DM Sans', sans-serif" }}>{step.number}</span>
                  {step.gate && (
                    step.non_negotiable_tooltip
                      ? <NonNegotiableTooltip text={step.non_negotiable_tooltip} />
                      : <span style={{ fontSize: 10, background: C.teal, color: C.tealBg, borderRadius: 50, padding: "2px 9px", fontWeight: 600, fontFamily: "'DM Sans', sans-serif", letterSpacing: "0.08em" }}>NON-NEGOTIABLE</span>
                  )}
                </div>
                <h2 style={{ fontSize: 22, fontFamily: "'Playfair Display', serif", fontWeight: 700, color: C.tealDark, marginBottom: 4, lineHeight: 1.3 }}>{step.label}</h2>
                <p style={{ fontSize: 13, color: C.charcoalLight, fontFamily: "'DM Sans', sans-serif", marginBottom: 22, lineHeight: 1.5 }}>
                  {!sessionCode && step.tagline_solo ? step.tagline_solo : step.tagline}
                </p>
                {renderStep()}
              </div>

              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 26, paddingTop: 18, borderTop: `1px solid ${C.tealBg}` }}>
                <button onClick={() => idx > 0 && go(-1)} style={{ background: "none", border: "none", cursor: idx > 0 ? "pointer" : "default", color: idx > 0 ? C.teal : C.charcoalBg, fontSize: 13, fontFamily: "'DM Sans', sans-serif", padding: "8px 0", fontWeight: 500 }}>← Back</button>
                <div style={{ display: "flex", gap: 8 }}>
                  {isLast && (
                    <>
                      <button onClick={() => exportSummary(answers, sessionCode, myRole, myName)} style={{ background: C.goldBg, border: `1.5px solid ${C.goldLight}`, borderRadius: 50, padding: "10px 16px", fontSize: 13, fontFamily: "'DM Sans', sans-serif", color: C.goldDark, cursor: "pointer", fontWeight: 600 }}>↓ Export</button>
                      {sessionCode && <button onClick={() => setScreen("couples_summary")} style={{ background: C.teal, color: "#fff", border: "none", borderRadius: 50, padding: "10px 18px", fontSize: 13, fontWeight: 600, fontFamily: "'DM Sans', sans-serif", cursor: "pointer" }}>Compare →</button>}
                      {!sessionCode && <button onClick={() => { localClear(); setSavedData(null); setScreen("welcome"); }} style={{ background: C.teal, color: "#fff", border: "none", borderRadius: 50, padding: "10px 18px", fontSize: 13, fontWeight: 600, fontFamily: "'DM Sans', sans-serif", cursor: "pointer" }}>Start over ↺</button>}
                    </>
                  )}
                  {!isLast && (
                    <button onClick={() => canGo() && go(1)} style={{ background: canGo() ? C.teal : C.charcoalBg, color: canGo() ? "#fff" : C.charcoalLight, border: "none", borderRadius: 50, padding: "12px 26px", fontSize: 14, fontWeight: 600, fontFamily: "'DM Sans', sans-serif", cursor: canGo() ? "pointer" : "default", transition: "all 0.2s" }}>Continue →</button>
                  )}
                </div>
              </div>
            </>
          )}

          {screen === "couples_summary" && (
            <>
              <CouplesSummary code={sessionCode} myRole={myRole} myName={myName} myAnswers={answers} />
              <div style={{ display: "flex", gap: 8, marginTop: 20, justifyContent: "flex-end" }}>
                <button onClick={() => exportSummary(answers, sessionCode, myRole, myName)} style={{ background: C.goldBg, border: `1.5px solid ${C.goldLight}`, borderRadius: 50, padding: "10px 16px", fontSize: 13, fontFamily: "'DM Sans', sans-serif", color: C.goldDark, cursor: "pointer", fontWeight: 600 }}>↓ Export</button>
                <button onClick={() => setScreen("session")} style={{ background: C.teal, color: "#fff", border: "none", borderRadius: 50, padding: "10px 18px", fontSize: 13, fontWeight: 600, fontFamily: "'DM Sans', sans-serif", cursor: "pointer" }}>← Back to steps</button>
              </div>
            </>
          )}
        </div>

        <p style={{ maxWidth: 500, marginTop: 22, fontSize: 12, color: C.teal, fontFamily: "'DM Sans', sans-serif", lineHeight: 1.8, textAlign: "center", padding: "0 8px", opacity: 0.7 }}>
          There is no option without loss. There is no option without discomfort.<br />
          But there is a path that better aligns with what you believe you're called to build.
        </p>
      </div>
    </>
  );
}
